/**
 * Stage 9 — ODA WebAssembly viewer.
 *
 * Pure renderer. Takes a Layout prop and renders it on top of the
 * template DWG. Data fetching + loading/error states live in the
 * route page (LayoutPage); this component is purely about boot,
 * device setup, OpenFile, entity creation from the payload, and
 * click-pick selection with highlight.
 *
 * SDK class is ``Module.App``. The Protected build logs
 * "Module.CadCore deprecated, please use Module.App" — App is the
 * superset, CadCore is a legacy alias.
 *
 * Lifetime: App is NOT Embind-disposable — do NOT call .delete()
 * on the App instance, nor on the OdDbDatabase returned by
 * app.getDb(). The browser tears the WASM heap down at page unload.
 *
 * Entity creation patterns mirror docs/oda-examples/01-basic:
 *   - example 30 (3dPolylineLinetype): polyline → setLayer →
 *     appendOdDbEntity *before* vertices → loop(create vertex →
 *     appendVertex → setPosition)
 *   - example 03 (LayoutBlock): block ref → setDatabaseDefaults →
 *     setLayer → appendOdDbEntity *before* setBlockTableRecord
 *   - example 07 (OpenAndSelect): screenToWorld via screenMatrix ×
 *     projectionMatrix inverse, then setCoordSystem(target, x, y, z);
 *     pick via OdDbSelectionSet.select1; highlight via
 *     entity.highlight(true, null, true)
 *
 * Stage H instrumentation (will be stripped when picking is solid):
 *   - [pick] canvas + world coordinate logging on every click
 *   - [pick] HIT / MISS logging with the selected entity id
 *   - tracker bbox diagnostic (carried over from Stage G.5)
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useEditOps } from "../../hooks/useEditOps";
import type { Layout } from "../../types/layout";
import { insertionToSw, swToInsertion } from "./coords";

// ── ODA Module global types ──────────────────────────────────────────
//
// We type only the surface Stages F/G/H touch. The SDK's full
// .d.ts (596 KB, 273 classes, parse-broken) lives in docs/oda-sdk/.

interface OdaPoint3d {
  x: number;
  y: number;
  z: number;
  transformBy(m: OdaGeMatrix3d): OdaPoint3d;
  asVector(): OdaGeVector3d;
  delete(): void;
}

interface OdaGeVector3d {
  normalize(tol: OdaGeTol): OdaGeVector3d;
  sub(v: OdaGeVector3d): OdaGeVector3d;
  crossProduct(v: OdaGeVector3d): OdaGeVector3d;
  delete(): void;
}

interface OdaGeMatrix3d {
  mul(m: OdaGeMatrix3d): OdaGeMatrix3d;
  inverse(): OdaGeMatrix3d;
  setCoordSystem(
    origin: OdaPoint3d,
    x: OdaGeVector3d,
    y: OdaGeVector3d,
    z: OdaGeVector3d,
  ): OdaGeMatrix3d;
  delete(): void;
}

interface OdaGeTol {
  delete(): void;
}

interface OdaGeExtents3d {
  minPoint(): OdaPoint3d;
  maxPoint(): OdaPoint3d;
  delete(): void;
}

interface OdaGePoint3dArray {
  push_back(p: OdaPoint3d): void;
  asArrayPtr(): unknown;
  size(): number;
  delete(): void;
}

interface OdaString {
  toString(): string;
  delete(): void;
}

interface OdaDbObjectIterator {
  done(): boolean;
  objectId(): OdaObjectId;
  step(forward: boolean, skipErased: boolean): void;
  delete(): void;
}

interface OdaObject {
  delete(): void;
}

interface OdaObjectId {
  safeOpenObject(mode: unknown, openErased: boolean): OdaObject;
  openObject1(mode: unknown, openErased: boolean): OdaObject;
  isNull(): boolean;
  // Stage L.4.d: the database handle of the entity this id refers to.
  // We stringify it (via OdDbHandle.ascii().toString()) and use it as
  // the canonical key in the WASM-handle → server-tracker-id Map.
  getHandle(): OdaDbHandle;
  delete(): void;
}

interface OdaDbHandle {
  ascii(): OdaString;
  isNull(): boolean;
  delete(): void;
}

interface OdaDbObjectIdArray {
  size(): number;
  get(i: number): OdaObjectId;
  delete(): void;
}

interface OdaDb {
  getModelSpaceId(): OdaObjectId;
  getBlockTableId(): OdaObjectId;
  activeViewportId(): OdaObjectId;
  // Stage L.5: persist database to VFS path (d.ts:4549). Confirmed
  // working at runtime — example 29's canonical save path. Round-trip
  // back to JS bytes goes through services.createStreamBuf opening the
  // same VFS path for read, then draining via getByte().
  writeFile1(
    filename: OdaString,
    fileType: number,
    fileVersion: number,
    saveThumbnailImage: boolean,
    dxfPrecision: number,
  ): void;
  // d.ts:4548 — second overload taking a streambuf. KEPT IN TYPING but
  // NOT CALLED at runtime: testing showed it returns in 0ms and leaves
  // the streambuf empty, because services.createStreamBuf(path) returns
  // a path-backed read-only stream that writeFile cannot fill. If a
  // future SDK build adds a writable in-memory streambuf factory, this
  // becomes the preferred path (avoids the VFS detour).
  writeFile(
    pStreamBuf: OdaStreamBuf,
    fileType: number,
    fileVersion: number,
    saveThumbnailImage: boolean,
    dxfPrecision: number,
  ): void;
  // d.ts:4551 — alternate save with simpler signature. Used in the
  // Stage L.5 diagnostic ladder alongside writeFile1.
  save1(filename: OdaString, saveThumbnailImage: boolean): void;
}

// Stage L.5: byte buffer abstraction (d.ts:3387). We use length()/rewind()/
// getByte() to drain DWG bytes after writeFile1 + re-open. NOTE: length()
// returns int64 in the C++ ABI, which Embind maps to JS BigInt — callers
// MUST convert with Number(...) before passing to Uint8Array constructor
// or comparing against a Number loop counter. Bulk-read methods
// (getBytes, read, copyTo to a JS array) are NOT in the documented
// surface, but Embind sometimes adds convenience accessors at runtime —
// phase 2 probes the instance prototype and uses them if present.
interface OdaStreamBuf {
  length(): number | bigint;
  rewind(): void;
  tell(): number | bigint;
  isEof(): boolean;
  getByte(): number;
  delete(): void;
}

// Stage L.5: ODA Services (d.ts:40). createStreamBuf(path) returns a
// path-backed readable streambuf — used to re-open a VFS file (just
// written via db.writeFile1) and pull its bytes back to JS. readFile(path)
// returns a fresh OdDbDatabase loaded from a VFS path; used in the
// fresh-template diagnostic to isolate save failures from db-state issues.
interface OdaServices {
  initialize(): void;
  uninitialize(): void;
  createStreamBuf(path: string): OdaStreamBuf;
  readFile(path: string): OdaDb;
  delete?(): void;
}

// Entity base. Per the SDK, OdDb3dPolyline / OdDbBlockReference / etc.
// all extend OdDbEntity. highlight is on OdDbEntity per d.ts:14680.
interface OdaDbEntity {
  setDatabaseDefaults(db: OdaDb, doSubents: boolean): void;
  setLayer(
    layerName: OdaString,
    doSubents: boolean,
    allowHiddenLayer: boolean,
  ): unknown;
  highlight(bDoIt: boolean, pSubId: null, highlightAll: boolean): void;
  erase(eraseIt: boolean): unknown;
  // ACI colour index. 256 = ByLayer (the tracker default), 6 = magenta
  // (our selection colour). Valid on every entity at runtime.
  setColorIndex(idx: number): void;
  // Visibility enum: 0 = kInvisible, 1 = kVisible. Hiding an entity is a
  // modification (safe), unlike erase() which crashes this WebGL context.
  setVisibility(visibility: number): void;
  delete(): void;
}

interface OdaDbLine extends OdaDbEntity {
  setStartPoint(p: OdaPoint3d): void;
  setEndPoint(p: OdaPoint3d): void;
}

interface OdaDb3dPolylineVertex {
  setDatabaseDefaults(db: OdaDb, doSubents: boolean): void;
  setPosition(p: OdaPoint3d): void;
  delete(): void;
}

interface OdaDb3dPolyline extends OdaDbEntity {
  appendVertex(v: OdaDb3dPolylineVertex): OdaObjectId;
  makeClosed(): void;
  setColorIndex(idx: number): void;
}

interface OdaDbBlockReference extends OdaDbEntity {
  setBlockTableRecord(id: OdaObjectId): void;
  setPosition(p: OdaPoint3d): void;
  // Stage L.4.d: read the current insertion point so the move handler
  // can snapshot it for rollback. d.ts:5907 shows position() on
  // OdDbBlockReference returning OdGePoint3d.
  position(): OdaPoint3d;
  getGeomExtents(extents: OdaGeExtents3d): void;
  blockTableRecord(): OdaObjectId;
}

interface OdaBlockTableRecord {
  appendOdDbEntity(e: OdaDbEntity): OdaObjectId;
  // newIterator: (atBeginning, skipDeleted, sorted) per d.ts:2193.
  newIterator(
    atBeginning: boolean,
    skipDeleted: boolean,
    sorted: boolean,
  ): OdaDbObjectIterator;
  getName(): OdaString;
  delete(): void;
}

interface OdaDbBlockTable {
  // getAt2 returns just the OdDbObjectId without auto-opening the record
  // (vs getAt which takes an OpenMode and opens the record itself).
  getAt2(recordName: OdaString, getErased: boolean): OdaObjectId;
  delete(): void;
}

interface OdaDbSelectionSet {
  objectIdArray(): OdaDbObjectIdArray;
  delete(): void;
}

interface OdaGsView {
  screenMatrix(): OdaGeMatrix3d;
  projectionMatrix(): OdaGeMatrix3d;
  worldToDeviceMatrix(): OdaGeMatrix3d;
  upVector(): OdaGeVector3d;
  position(): OdaPoint3d;
  target(): OdaPoint3d;
  delete(): void;
}

interface OdaDevice {
  setBackgroundColor(color: number): void;
  setLogicalPalette(palette: unknown, count: number): void;
  viewAt(index: number): OdaGsView;
  numViews(): number;
  // Invalidate the WHOLE device surface → the next App.Update() does a
  // FULL redraw instead of an incremental/partial update. The partial
  // path composites through a broken MRT (drawBuffersWEBGL) on this
  // context, so after any DB edit we force a full redraw via this.
  invalidate(): void;
}

// Graphics-system model. onErased notifies the cache that a single
// drawable was erased so the next Update drops just that one (light) —
// avoids regenAll(), which re-vectorizes the whole scene (~1.7s) and
// trips the GPU watchdog → WebGL context loss.
interface OdaGsModel {
  onErased(pErased: unknown, pParent: unknown): void;
  delete(): void;
}

interface OdaApp {
  OpenFile(name: string): void;
  createDevice(): void;
  getDevice(): OdaDevice;
  getGsModel(): OdaGsModel;
  setDb(db: OdaDb): void;
  getDb(): OdaDb;
  ExecuteCommand(cmd: string): unknown;
  Update(): void;
  Resize(w: number, h: number): void;
  Orbit(dx: number, dy: number): void;
  Dolly(dx: number, dy: number): void;
  Zoom(factor: number, scrx: number, scry: number): void;
  ZoomExtents(): void;
  SetUserName(name: string): void;
  regenAll(): void;
  gsModule(): unknown;
  registerFontFileName(name: string): void;
  enableCustomHighlighting(enable: boolean): void;
  resetHighlightColor(): void;
  setRenderMode(mode: number): void;
}

interface OdaModule {
  App: new () => OdaApp;
  OdString: new (s: string) => OdaString;
  OdDbLine: { createObject(): OdaDbLine };
  OdGePoint3d: new (x: number, y: number, z: number) => OdaPoint3d;
  OdGeMatrix3d: new () => OdaGeMatrix3d;
  OdGeTol: new (tolerance: number) => OdaGeTol;
  OdGePoint3dArray: new () => OdaGePoint3dArray;
  OdGeExtents3d: new () => OdaGeExtents3d;
  OdDbEntity: { cast(obj: OdaObject): OdaDbEntity | null };
  OdDbBlockTableRecord: { cast(obj: OdaObject): OdaBlockTableRecord };
  OdDbBlockTable: { cast(obj: OdaObject): OdaDbBlockTable };
  OdDb3dPolyline: { createObject(): OdaDb3dPolyline };
  OdDb3dPolylineVertex: { createObject(): OdaDb3dPolylineVertex };
  OdDbBlockReference: {
    createObject(): OdaDbBlockReference;
    cast(obj: OdaObject): OdaDbBlockReference | null;
  };
  OdDbSelectionSet: {
    select1(
      vpId: OdaObjectId,
      nPoints: number,
      wcsPts: unknown,
      mode: number,
      sm: number,
      pFilter: null,
    ): OdaDbSelectionSet;
  };
  // Stage L.5: OdStreamBuf class object and Services constructor for the
  // save path. Both are present in the SDK exports (OdStreamBuf was in
  // the phase-0 candidates list); Services is the standard ODA host.
  OdStreamBuf?: { createObject(): OdaStreamBuf };
  Services?: new () => OdaServices;
  odcmAcadPalette?: (backgroundColor: number) => unknown;
  getExceptionMessage?: (e: unknown) => unknown;
  canvas: HTMLCanvasElement | null;
  calledRun?: boolean;
  preRun: Array<() => void>;
  postRun: Array<() => void>;
  ASSETS_FOLDER: string;
  FS_createDataFile(
    parent: string,
    name: string,
    data: Uint8Array,
    canRead: boolean,
    canWrite: boolean,
    canOwn: boolean,
  ): void;
  // Stage L.5: this SDK build filters out the full Emscripten FS namespace
  // but exposes the FS surface in three possible forms; the adapters in
  // handleSaveAsDwg pick whichever is callable. Per the live ODA docs at
  // cloud.opendesign.com/docs/drawingapi/modules.html, `_FS` is the
  // primary entry point on this build — likely the full namespace under
  // a non-standard Emscripten name. FS_createDataFile (no underscore) is
  // also definitely present — it's used for the template load.
  _FS?: OdaGlobalFS;
  FS?: OdaGlobalFS;
  FS_readFile?: (path: string) => Uint8Array;
  FS_unlink?: (path: string) => void;
  FS_writeFile?: (path: string, data: Uint8Array) => void;
  // Stage L.5: SaveType/DwgVersion enums are declared in DrawingWeb.d.ts
  // (namespace Module, lines 7866 and 11001) but the actual Embind build
  // does NOT expose them as namespace objects on Module. Runtime probe
  // confirms typeof mod.SaveType === 'undefined'. We pass raw ints
  // directly in handleSaveAsDwg (SaveType.kDwg = 0, DwgVersion.kDHL_CURRENT
  // = 33). These typings are kept optional so the phase-0 probe still
  // type-checks against the property name.
  SaveType?: { kDwg: number; kDxf: number; kDxb: number; kUnknown: number };
  DwgVersion?: { kDHL_CURRENT: number };
}

interface OdaGlobalFS {
  analyzePath(path: string): { exists: boolean };
  mkdir(path: string): void;
  // Stage L.5: read/unlink/stat are NOT guaranteed in this build —
  // notation.js exposes window.FS but only some of the standard Emscripten
  // FS surface. Optional so we can typeof-probe before calling.
  readFile?(path: string): Uint8Array;
  unlink?(path: string): void;
  stat?(path: string): { size: number };
}

// Enums populated by notation.js as top-level `var X = {…}` declarations,
// which become window.X at runtime. notation.js loads synchronously
// before DrawingWeb.js per index.html script order, so they are
// guaranteed by the time waitForModuleReady() resolves.
declare global {
  interface Window {
    Module?: OdaModule;
    FS?: OdaGlobalFS;
    OpenMode: { kForWrite: number; kForRead: number; kForNotify: number };
    Mode: {
      kPoint: number;
      kBox: number;
      kWindow: number;
      kCrossing: number;
      kFence: number;
      kWPoly: number;
      kCPoly: number;
    };
    SubentSelectionMode: {
      kDisableSubents: number;
      kEnableSubents: number;
    };
  }
}

const ASSETS = "/assets";
const TEMPLATE_NAME = "template.dwg";
const TEMPLATE_PATH = `${ASSETS}/${TEMPLATE_NAME}`;
const WHITE = 0xffffff;

// Locked Stage 9 placement formula: tracker SW corner → block insertion
// point offset (see backend/app/routes/layout.py + plan section 2).
const PLACEMENT_OFFSET_X = 1.139;
const PLACEMENT_OFFSET_Y = 60.1264;
const TRACKER_BLOCK_NAME = "108_INT";
const BOUNDARY_LAYER = "Boundary";
const TRACKERS_LAYER = "Racks";

// Click-vs-drag threshold: a LMB press+release whose mouseup is within
// this many pixels of mousedown counts as a click; anything more is
// treated as a drag (already wired to Dolly in onMouseMove).
const CLICK_DRAG_PX = 4;


const loadingSpinnerStyle: React.CSSProperties = {
  display: "inline-block",
  width: 18,
  height: 18,
  border: "2px solid #c7d2fe",
  borderTopColor: "#4338ca",
  borderRadius: "50%",
  animation: "spark-spin 0.8s linear infinite",
};

/** Human "Placing N trackers across M parcels · K roads · J inverters…"
 * line for the loading overlay, with real counts from the layout. */
function describeLayout(layout: Layout): string {
  const nTr = layout.trackers.length;
  const nB = layout.boundaries?.length ?? 1;
  let s = `Placing ${nTr.toLocaleString()} tracker${nTr === 1 ? "" : "s"} across ${nB} parcel${nB === 1 ? "" : "s"}`;
  const extras: string[] = [];
  if (layout.roads?.length)
    extras.push(`${layout.roads.length} road${layout.roads.length === 1 ? "" : "s"}`);
  if (layout.inverters?.length)
    extras.push(
      `${layout.inverters.length} inverter${layout.inverters.length === 1 ? "" : "s"}`,
    );
  if (extras.length) s += ` · ${extras.join(" · ")}`;
  return `${s}…`;
}

// Wait for WASM postRun to fire. The WASM script (DrawingWeb.js) is
// loaded by index.html and starts running before any React code; by the
// time the component mounts, the module is either still loading
// (postRun hasn't fired) or fully ready (calledRun is true).
function waitForModuleReady(): Promise<OdaModule> {
  const mod = window.Module;
  if (!mod) {
    return Promise.reject(
      new Error(
        "window.Module not initialized — check that DrawingWeb.js loaded.",
      ),
    );
  }
  if (mod.calledRun) return Promise.resolve(mod);
  return new Promise<OdaModule>((resolve) => {
    mod.postRun.push(() => resolve(mod));
  });
}

// Background + palette setup. createDevice() is called by the caller
// BEFORE setupDevice. Failures here are best-effort: a missing
// odcmAcadPalette doesn't take down the working setBackgroundColor.
function setupDevice(app: OdaApp, mod: OdaModule): void {
  let device: OdaDevice;
  try {
    device = app.getDevice();
    device.setBackgroundColor(WHITE);
  } catch (err) {
    console.warn("[ODAViewer] Device setup failed:", err);
    return;
  }
  try {
    if (typeof mod.odcmAcadPalette === "function") {
      device.setLogicalPalette(mod.odcmAcadPalette(WHITE), 256);
    } else {
      console.warn(
        "[ODAViewer] Module.odcmAcadPalette is not a function; skipping setLogicalPalette",
      );
    }
  } catch (err) {
    console.warn("[ODAViewer] setLogicalPalette failed:", err);
  }

  // Disable GPU-based selection. The GPU-selection render path draws the
  // scene to an offscreen ID buffer using MULTIPLE RENDER TARGETS
  // (glDrawBuffers). This WebGL context can't satisfy that MRT setup, so
  // every frame throws ``drawBuffersWEBGL: INVALID_OPERATION
  // (COLOR_ATTACHMENTi_EXT or NONE)`` — the persistent error that, after
  // an edit, leaves the canvas unable to present a frame (the "freeze").
  // CPU selection needs no extra render target. Mirrors the "GPU
  // Selection" toggle in ODA's SelectByObjectHandle example.
  try {
    const m = mod as unknown as {
      OdString: new (s: string) => unknown;
      OdRxVariant: { cast(o: unknown): unknown };
      OdRxVariantValue: {
        cast(o: unknown): { asOdVariant(): { setBool(b: boolean): void } };
      };
      ODCOLORREF: new () => unknown;
    };
    const dev = device as unknown as {
      properties(): {
        getAt(key: unknown): { get(): unknown };
        putAt(key: unknown, val: unknown, def: unknown): void;
      };
    };
    const props = dev.properties();
    const ptr = props.getAt(new m.OdString("GPUSelectionEnable"));
    const variant = m.OdRxVariantValue.cast(
      m.OdRxVariant.cast(ptr.get()),
    ).asOdVariant();
    variant.setBool(false);
    props.putAt(
      new m.OdString("GPUSelectionEnable"),
      ptr.get(),
      new m.ODCOLORREF(),
    );
    console.log("[ODAViewer] GPU selection DISABLED (avoids MRT drawBuffers)");
  } catch (err) {
    console.warn("[ODAViewer] could not disable GPU selection:", err);
  }
}

// Iterate model space and count block references whose referenced
// block matches `blockName`. Called after every mutation (initial
// layout load, delete, future add) so the panel reflects the DB.
// All WASM-allocated handles are .delete()-ed.
function recountTrackers(
  app: OdaApp,
  mod: OdaModule,
  blockName: string,
): number {
  console.time("[perf] recountTrackers");
  let count = 0;
  try {
    const db = app.getDb();
    const modelSpaceId = db.getModelSpaceId();
    const pObj = modelSpaceId.safeOpenObject(window.OpenMode.kForRead, false);
    const pRecord = mod.OdDbBlockTableRecord.cast(pObj);
    pObj.delete();

    // (atBeginning=true, skipDeleted=true, sorted=false) per example 19.
    const iter = pRecord.newIterator(true, true, false);

    // Total-entities counter distinguishes "iterator sees 0 entities"
    // (database not settled) from "iterator sees N entities but 0
    // matched the block name" (filtering / name-compare issue).
    let totalEntitiesSeen = 0;

    while (!iter.done()) {
      totalEntitiesSeen++;
      const entityId = iter.objectId();

      const pEntObj = entityId.safeOpenObject(
        window.OpenMode.kForRead,
        false,
      );
      const pBlockRef = mod.OdDbBlockReference.cast(pEntObj);
      pEntObj.delete();

      if (pBlockRef) {
        const btrId = pBlockRef.blockTableRecord();
        const pBtrObj = btrId.safeOpenObject(
          window.OpenMode.kForRead,
          false,
        );
        const pBtr = mod.OdDbBlockTableRecord.cast(pBtrObj);
        pBtrObj.delete();

        const name = pBtr.getName();
        const nameStr = name.toString();
        if (nameStr === blockName) count++;
        name.delete();

        pBtr.delete();
        btrId.delete();
        pBlockRef.delete();
      }

      entityId.delete();
      // (forward=true, skipErased=true) per example 19.
      iter.step(true, true);
    }

    iter.delete();
    pRecord.delete();
    console.log(
      "[count] iterator saw",
      totalEntitiesSeen,
      "total entities, of which",
      count,
      "matched",
      blockName,
    );
  } catch (e) {
    console.error("[count] EXCEPTION:", e, String(e));
  } finally {
    console.timeEnd("[perf] recountTrackers");
  }
  return count;
}

// Screen-to-world transform — ported verbatim from
// docs/oda-examples/01-basic/07-open-and-select/code.js. Two steps:
//   1) Invert screenMatrix × projectionMatrix to map screen → eye
//   2) setCoordSystem(target, xVec, yVec, zVec) for eye → world
// The example leaks intermediate matrices/vectors per call; we accept
// the same low-rate leak (one click ≈ a handful of small WASM objects).
function screenToWorld(
  mod: OdaModule,
  view: OdaGsView,
  x: number,
  y: number,
): OdaPoint3d {
  const point = new mod.OdGePoint3d(x, y, 0);

  // Step 1: screen → eye
  point.transformBy(view.screenMatrix().mul(view.projectionMatrix()).inverse());
  point.z = 0;

  // Step 2: eye → world
  const xEyeToWorld = new mod.OdGeMatrix3d();
  const tol = new mod.OdGeTol(1e-10);
  const yVector = view.upVector().normalize(tol);
  const zVector = view
    .position()
    .asVector()
    .sub(view.target().asVector())
    .normalize(tol);
  const xVector = yVector.crossProduct(zVector);
  xEyeToWorld.setCoordSystem(view.target(), xVector, yVector, zVector);
  point.transformBy(xEyeToWorld);

  return point;
}

// Add the boundary polyline + tracker block references to the open
// database. Per the SDK examples:
//   - Polyline (example 30): append polyline to BTR BEFORE creating
//     vertices. For each vertex: createObject → setDatabaseDefaults →
//     appendVertex(v) → setPosition (AFTER appendVertex).
//   - Block reference (example 03): append block-ref to BTR BEFORE
//     setBlockTableRecord.
// Every WASM-allocated object is .delete()-ed per ODA gotcha #1.
// regenAll is omitted — throws on this SDK after entity append, and
// the outer init's app.Update + app.ZoomExtents handle invalidation.
function addLayoutToCanvas(
  app: OdaApp,
  mod: OdaModule,
  layout: Layout,
  /**
   * Stage L.4.d callback — called once per tracker block-ref created
   * during the bulk load. Populates the WASM-handle → server-tracker-id
   * map in the parent component. Caller is responsible for stringifying
   * the handle consistently with how handlePick / handleMove /
   * handleDelete look up entries later.
   */
  onTracker?: (handleStr: string, trackerId: string) => void,
): void {
  console.time("[perf] addLayoutToCanvas total");
  console.log(
    "[perf] entity counts: 1 boundary +",
    layout.trackers.length,
    "trackers",
  );
  try {
    console.log(
      "[layout] start, boundary points:",
      layout.boundary.points.length,
      "trackers:",
      layout.trackers.length,
    );

    const db = app.getDb();

    // ---- Open model space for write ----
    const modelSpaceId = db.getModelSpaceId();
    const pObj = modelSpaceId.safeOpenObject(window.OpenMode.kForWrite, false);
    const pRecord = mod.OdDbBlockTableRecord.cast(pObj);
    pObj.delete();

    // Draw one colored polyline using ONLY the operations the boundary +
    // tracker drawing already use safely (createObject / appendVertex /
    // setColorIndex). No layer-table writes and no setLayer to a custom
    // name — both are unproven SDK surfaces that can hard-trap the WASM
    // engine. The ACI color alone makes each feature type distinct.
    const drawColoredPolyline = (
      coords: Array<[number, number]>,
      closed: boolean,
      colorIndex: number,
    ): void => {
      if (coords.length < 2) return;
      const pPoly = mod.OdDb3dPolyline.createObject();
      pPoly.setDatabaseDefaults(db, false);
      pRecord.appendOdDbEntity(pPoly);
      try {
        pPoly.setColorIndex(colorIndex);
      } catch {
        /* color is best-effort */
      }
      for (const [x, y] of coords) {
        const v = mod.OdDb3dPolylineVertex.createObject();
        v.setDatabaseDefaults(db, false);
        pPoly.appendVertex(v);
        const pt = new mod.OdGePoint3d(x, y, 0);
        v.setPosition(pt);
        pt.delete();
        v.delete();
      }
      if (closed) pPoly.makeClosed();
      pPoly.delete();
    };

    // Draw exclusions (red), KML roads (amber), POIs (blue marker). ACI
    // colors: 1=red, 40=amber, 5=blue. Per-feature guarded.
    const drawSiteFeatures = (
      sf: NonNullable<Layout["siteFeatures"]>,
    ): void => {
      const draw = (
        coords: Array<[number, number]>,
        closed: boolean,
        color: number,
      ): void => {
        try {
          drawColoredPolyline(coords, closed, color);
        } catch (e) {
          console.warn("[layout] site feature draw skipped:", e);
        }
      };
      // Close ONLY genuine polygons — an exclusion/road can be a LineString
      // (open path), which must NOT be closed or it renders as a filled area.
      for (const f of sf.exclusions) draw(f.coords, f.kind === "polygon", 1);
      for (const f of sf.roads) draw(f.coords, f.kind === "polygon", 40);
      const M = 4; // half-size of the POI square marker
      for (const f of sf.pois) {
        const c = f.coords[0];
        if (!c) continue;
        const [x, y] = c;
        draw(
          [
            [x - M, y - M],
            [x + M, y - M],
            [x + M, y + M],
            [x - M, y + M],
          ],
          true,
          5,
        );
      }
    };

    // ---- Boundary polyline(s) ----
    // A multi-parcel site plan carries every boundary ring in
    // ``layout.boundaries``; older / single-boundary runs only have
    // ``layout.boundary``. Draw one polyline per ring so EVERY parcel is
    // outlined, not just the first.
    const boundaryRings =
      layout.boundaries && layout.boundaries.length > 0
        ? layout.boundaries
        : [layout.boundary];
    console.log("[layout] phase 1-5: drawing", boundaryRings.length, "boundary ring(s)");
    for (const ring of boundaryRings) {
      const pPoly = mod.OdDb3dPolyline.createObject();
      pPoly.setDatabaseDefaults(db, false);

      const boundaryLayerName = new mod.OdString(BOUNDARY_LAYER);
      pPoly.setLayer(boundaryLayerName, false, false);
      boundaryLayerName.delete();

      // CRITICAL (example 30): append polyline BEFORE its vertices.
      pRecord.appendOdDbEntity(pPoly);

      for (const pt of ring.points) {
        const vertex = mod.OdDb3dPolylineVertex.createObject();
        vertex.setDatabaseDefaults(db, false);
        // append BEFORE setPosition (example 30 pattern).
        pPoly.appendVertex(vertex);
        const point = new mod.OdGePoint3d(pt.x, pt.y, pt.z);
        vertex.setPosition(point);
        point.delete();
        vertex.delete();
      }

      if (ring.closed) {
        pPoly.makeClosed();
      }

      pPoly.delete();
    }

    // ---- Block-table lookup helper (cached per name) ----
    // Stage 10: trackers may reference 108_INT (most), 81_EPN (cols 19+20
    // of AC blocks), and inverters reference INV_PAD. Cache the resolved
    // block-table-record ObjectId per name; warn-and-skip names that
    // aren't present in the template database (so a missing INV_PAD
    // doesn't crash the whole render — you just don't see inverters).
    console.log("[layout] phase 6: getBlockTableId + cast OdDbBlockTable");
    const blockTableId = db.getBlockTableId();
    const pBtObj = blockTableId.safeOpenObject(window.OpenMode.kForRead, false);
    const pBlockTable = mod.OdDbBlockTable.cast(pBtObj);
    pBtObj.delete();

    // DIAGNOSTIC pass — enumerate the BlockTable purely to log every
    // record name so we can see exactly what ODA stores. ObjectIds
    // returned by the iterator are NOT stable past iterator.delete(),
    // which is why we don't cache them — we use getAt2 below to fetch
    // stable ids by the original-case name.
    const blockNamesSeen: string[] = [];
    try {
      // BlockTable iterator surface isn't in the local d.ts slice yet;
      // assert a structural shape so TS doesn't complain.
      interface BlockTableIter {
        start: (atBeginning: boolean, mode: number) => void;
        done: () => boolean;
        step: (forward: boolean, skipDeleted: boolean) => void;
        getRecordId: () => {
          safeOpenObject: (mode: number, openErased: boolean) => unknown;
          delete: () => void;
        };
        delete: () => void;
      }
      const tableWithIter = pBlockTable as unknown as {
        newIterator: () => BlockTableIter;
      };
      const iter = tableWithIter.newIterator();
      for (iter.start(false, window.OpenMode.kForRead); !iter.done(); iter.step(true, false)) {
        const recId = iter.getRecordId();
        const recObj = recId.safeOpenObject(window.OpenMode.kForRead, false);
        const btr = mod.OdDbBlockTableRecord.cast(recObj as never);
        const rawName = btr.getName();
        const name = typeof rawName === "string" ? rawName : rawName.toString();
        blockNamesSeen.push(name);
        btr.delete();
        (recObj as { delete: () => void }).delete();
        recId.delete();
      }
      iter.delete();
      console.log("[diag] BlockTable: " + blockNamesSeen.length + " records");
      const hits = blockNamesSeen.filter((n) => /(?:81|108|inv|epn|pad)/i.test(n));
      console.log("[diag] tracker/inverter-shaped names: " + JSON.stringify(hits));
    } catch (e) {
      console.error("[diag] BlockTable enumeration failed:", e);
    }

    // resolveBlock — back to the proven Stage 9 pattern of pBlockTable.getAt2(name).
    // Try the requested name first, then case-variants. If a variant
    // succeeds, log which one so we can lock the right constant in the
    // backend. Cache by the requested name to avoid repeated lookups.
    const blockIdCache = new Map<string, ReturnType<typeof pBlockTable.getAt2>>();
    function resolveBlock(name: string): ReturnType<typeof pBlockTable.getAt2> | null {
      const cached = blockIdCache.get(name);
      if (cached !== undefined) {
        return cached.isNull() ? null : cached;
      }
      // Pull the list of unique candidate strings to try. blockNamesSeen
      // gives us every actual record name; pick the ones whose
      // lowercase form matches the requested name (case-insensitive)
      // or contains its longest alpha token (fuzzy for *_v2 etc.).
      const wanted = name.toLowerCase();
      const token = (wanted.match(/[a-z]{3,}/) ?? [wanted])[0];
      const candidates: string[] = [];
      candidates.push(name);
      candidates.push(name.toUpperCase());
      candidates.push(name.toLowerCase());
      for (const stored of blockNamesSeen) {
        const lo = stored.toLowerCase();
        if (lo === wanted || lo.includes(token)) candidates.push(stored);
      }
      // De-dupe while preserving order.
      const tried = new Set<string>();
      for (const candidate of candidates) {
        if (tried.has(candidate)) continue;
        tried.add(candidate);
        const ns = new mod.OdString(candidate);
        const id = pBlockTable.getAt2(ns, false);
        ns.delete();
        if (!id.isNull()) {
          if (candidate !== name) {
            console.log(
              `[layout] block "${name}" resolved via "${candidate}"`,
            );
          }
          blockIdCache.set(name, id);
          return id;
        }
        id.delete();
      }
      console.warn(
        `[layout] block "${name}" not found in template (tried: ${JSON.stringify([...tried])})`,
      );
      // Store the null sentinel via a fresh getAt2 of the original name
      // so the cache reflects "permanently not found" without dangling refs.
      const ns = new mod.OdString(name);
      const nullId = pBlockTable.getAt2(ns, false);
      ns.delete();
      blockIdCache.set(name, nullId);
      return null;
    }

    // Stage 10 polyline fallback for block names that don't exist in
    // the template. Keeps the AC-block visual readable until ``81_EPN``
    // and ``INV_PAD`` are added to template.dwg. Each rectangle is a
    // closed OdDb3dPolyline on the supplied layer. SW-anchored helper
    // expects (sw_x, sw_y, w, h); centroid-anchored helper expects
    // (cx, cy, w, d) and computes the four corners.
    function drawRectPolylineSW(
      sw_x: number,
      sw_y: number,
      w: number,
      h: number,
      layerName: InstanceType<typeof mod.OdString>,
    ): void {
      const pRect = mod.OdDb3dPolyline.createObject();
      pRect.setDatabaseDefaults(db, false);
      pRect.setLayer(layerName, false, false);
      pRecord.appendOdDbEntity(pRect);
      const corners: [number, number][] = [
        [sw_x, sw_y],
        [sw_x + w, sw_y],
        [sw_x + w, sw_y + h],
        [sw_x, sw_y + h],
      ];
      for (const [cx, cy] of corners) {
        const v = mod.OdDb3dPolylineVertex.createObject();
        v.setDatabaseDefaults(db, false);
        pRect.appendVertex(v);
        const p = new mod.OdGePoint3d(cx, cy, 0);
        v.setPosition(p);
        p.delete();
        v.delete();
      }
      pRect.makeClosed();
      pRect.delete();
    }

    // Hardcoded sizes from spec §1 — used only by the polyline
    // fallback. The real block-refs (when present in the template) get
    // their dimensions from the block-table records themselves.
    const FALLBACK_TRACKER_W = 2.278;
    const FALLBACK_L_81 = 97.7866;
    const FALLBACK_L_108 = 129.668;
    const FALLBACK_INV_W = 6.058;
    const FALLBACK_INV_D = 2.896;
    function fallbackTrackerLength(blockName: string): number {
      if (blockName === "81_EPN") return FALLBACK_L_81;
      return FALLBACK_L_108;
    }

    // ---- Tracker block references (multi-block, Stage 10 aware) ----
    console.log(
      "[layout] phase 7: create",
      layout.trackers.length,
      "tracker block references on layer",
      TRACKERS_LAYER,
    );
    const racksLayerName = new mod.OdString(TRACKERS_LAYER);
    console.time("[perf] tracker block-ref loop");
    const offsets = layout.placement_offset;
    // Stage 10 DIAGNOSTIC — verify the placement_offset map shape +
    // the computed insertion point for tracker[0] / inverter[0] /
    // road[0]. Remove once the AC-block render is verified.
    const t0 = layout.trackers[0];
    if (t0) {
      const ins0 = swToInsertion(t0.sw_x, t0.sw_y, offsets, t0.block ?? TRACKER_BLOCK_NAME);
      console.log("[diag] layout.placement_offset:", JSON.stringify(offsets));
      console.log("[diag] tracker[0]:", JSON.stringify(t0));
      console.log("[diag] tracker[0] insertion:", JSON.stringify(ins0));
    }
    if (layout.inverters && layout.inverters[0]) {
      const i0 = layout.inverters[0];
      const ins = swToInsertion(i0.cx, i0.cy, offsets, i0.block);
      console.log("[diag] inverter[0]:", JSON.stringify(i0));
      console.log("[diag] inverter[0] insertion:", JSON.stringify(ins));
    }
    if (layout.roads && layout.roads[0]) {
      console.log(
        "[diag] road[0] type=" + layout.roads[0].type + " first vertex=" + JSON.stringify(layout.roads[0].poly[0]) + " vertex count=" + layout.roads[0].poly.length,
      );
    }
    let fallbackTrackers = 0;
    // Resolve the canonical 108_INT block once so we can use it as the
    // visual fallback for any unresolvable tracker block (81_EPN today).
    // Per user direction 2026-06-22: render with the SAME settings as
    // 108_INT — not a cyan polyline placeholder.
    const block108Id = resolveBlock(TRACKER_BLOCK_NAME);
    type ColorableBlockRef = ReturnType<typeof mod.OdDbBlockReference.createObject> & {
      setColorIndex?: (idx: number) => void;
      setColor?: (color: { delete: () => void }) => void;
    };
    type CmColorCtorForBlock = new () => {
      setRGB: (r: number, g: number, b: number) => void;
      delete: () => void;
    };
    const cmColorCtorTrk = (mod as unknown as { OdCmColor?: CmColorCtorForBlock }).OdCmColor;
    for (const t of layout.trackers) {
      const blockName = t.block ?? TRACKER_BLOCK_NAME;
      let blockId = resolveBlock(blockName);
      if (blockId === null) {
        if (block108Id === null) {
          // 108_INT itself is missing — last-resort polyline so something draws.
          drawRectPolylineSW(
            t.sw_x,
            t.sw_y,
            FALLBACK_TRACKER_W,
            fallbackTrackerLength(blockName),
            racksLayerName,
          );
          fallbackTrackers += 1;
          continue;
        }
        // Use 108_INT block-ref styling for the missing-block case.
        blockId = block108Id;
      }
      const pRef = mod.OdDbBlockReference.createObject() as ColorableBlockRef;
      pRef.setDatabaseDefaults(db, false);
      pRef.setLayer(racksLayerName, false, false);
      // CRITICAL (example 03): append BEFORE setBlockTableRecord.
      const objId = pRecord.appendOdDbEntity(pRef);
      pRef.setBlockTableRecord(blockId);
      const ins = swToInsertion(t.sw_x, t.sw_y, offsets, blockName);
      const pos = new mod.OdGePoint3d(ins.x, ins.y, 0);
      pRef.setPosition(pos);
      pos.delete();

      // Stage 11 — grey out trackers that aren't part of any complete
      // AC block (block_id === null from the per-bay flood grouping).
      if (t.block_id === null) {
        if (cmColorCtorTrk && pRef.setColor) {
          try {
            const c = new cmColorCtorTrk();
            c.setRGB(160, 160, 160);
            pRef.setColor(c);
            c.delete();
          } catch {
            if (pRef.setColorIndex) pRef.setColorIndex(8);
          }
        } else if (pRef.setColorIndex) {
          pRef.setColorIndex(8);
        }
      }

      if (onTracker) {
        const h = objId.getHandle();
        const ascii = h.ascii();
        onTracker(ascii.toString(), t.id);
        ascii.delete();
        h.delete();
      }
      objId.delete();
      pRef.delete();
    }
    console.timeEnd("[perf] tracker block-ref loop");
    if (fallbackTrackers > 0) {
      console.warn(
        `[layout] drew ${fallbackTrackers} trackers as polyline fallback (missing block defs)`,
      );
    }
    racksLayerName.delete();

    // ---- Stage 10: inverter block references ----
    if (layout.inverters && layout.inverters.length > 0) {
      console.log(
        "[layout] phase 8: create",
        layout.inverters.length,
        "inverter block references on layer",
        TRACKERS_LAYER,
      );
      const invLayerName = new mod.OdString(TRACKERS_LAYER);
      let fallbackInv = 0;
      for (const inv of layout.inverters) {
        let blockId = resolveBlock(inv.block);
        if (blockId === null) {
          if (block108Id === null) {
            drawRectPolylineSW(
              inv.cx - FALLBACK_INV_W / 2,
              inv.cy - FALLBACK_INV_D / 2,
              FALLBACK_INV_W,
              FALLBACK_INV_D,
              invLayerName,
            );
            fallbackInv += 1;
            continue;
          }
          // Render with the SAME settings as 108_INT — per user direction.
          blockId = block108Id;
        }
        const pRef = mod.OdDbBlockReference.createObject();
        pRef.setDatabaseDefaults(db, false);
        pRef.setLayer(invLayerName, false, false);
        pRecord.appendOdDbEntity(pRef);
        pRef.setBlockTableRecord(blockId);
        // Inverter pad is centroid-anchored: centroid coincides with
        // insertion point (no offset). If the Inverter record uses a
        // different anchor in the template, ``placement_offset["Inverter"]``
        // applies the correction here.
        const ins = swToInsertion(inv.cx, inv.cy, offsets, inv.block);
        const pos = new mod.OdGePoint3d(ins.x, ins.y, 0);
        pRef.setPosition(pos);
        pos.delete();
        pRef.delete();
      }
      if (fallbackInv > 0) {
        console.warn(
          `[layout] drew ${fallbackInv} inverters as polyline fallback (108_INT also missing)`,
        );
      }
      invLayerName.delete();
    }

    // ---- Stage 10: road polylines on a Roads layer ----
    if (layout.roads && layout.roads.length > 0) {
      console.log(
        "[layout] phase 9: create",
        layout.roads.length,
        "road polylines on layer Roads",
      );
      const roadsLayerName = new mod.OdString("Roads");
      // Brown — saddle-brown RGB(139, 69, 19) via OdCmColor true-color
      // if the SDK exposes it, else ACI 33 (a brown shade in the
      // standard AutoCAD palette) as a fallback. Set per-entity so the
      // color is visible regardless of the Roads layer's authored
      // styling.
      type EntityWithColor = ReturnType<typeof mod.OdDb3dPolyline.createObject> & {
        setColorIndex?: (idx: number) => void;
        setColor?: (color: { delete: () => void }) => void;
      };
      type CmColorCtor = new () => {
        setRGB: (r: number, g: number, b: number) => void;
        delete: () => void;
      };
      const cmColorCtor = (mod as unknown as { OdCmColor?: CmColorCtor }).OdCmColor;
      for (const road of layout.roads) {
        if (road.poly.length < 2) continue;
        const pRoad = mod.OdDb3dPolyline.createObject() as EntityWithColor;
        pRoad.setDatabaseDefaults(db, false);
        pRoad.setLayer(roadsLayerName, false, false);
        pRecord.appendOdDbEntity(pRoad);
        for (const pt of road.poly) {
          const vertex = mod.OdDb3dPolylineVertex.createObject();
          vertex.setDatabaseDefaults(db, false);
          pRoad.appendVertex(vertex);
          const p = new mod.OdGePoint3d(pt.x, pt.y, 0);
          vertex.setPosition(p);
          p.delete();
          vertex.delete();
        }
        // Road polys arrive closed (first vertex == last) but call
        // makeClosed defensively in case a producer ever omits the
        // duplicate-closing vertex.
        pRoad.makeClosed();
        // Apply brown — try true-color first, fall back to ACI 33.
        if (cmColorCtor && pRoad.setColor) {
          try {
            const c = new cmColorCtor();
            c.setRGB(139, 69, 19);
            pRoad.setColor(c);
            c.delete();
          } catch {
            if (pRoad.setColorIndex) pRoad.setColorIndex(33);
          }
        } else if (pRoad.setColorIndex) {
          pRoad.setColorIndex(33);
        }
        pRoad.delete();
      }
      roadsLayerName.delete();
    }

    // ---- KML site features on their own named layers ----
    // Exclusions / roads / POIs from the site plan, drawn as their true
    // shapes so the operator can SEE what shaped the layout. Guarded so a
    // failure here never blocks boundary + tracker rendering.
    if (layout.siteFeatures) {
      try {
        drawSiteFeatures(layout.siteFeatures);
      } catch (e) {
        console.warn("[layout] site-features draw skipped:", e);
      }
    }

    // Release cached block-table records.
    for (const id of blockIdCache.values()) id.delete();
    pBlockTable.delete();

    pRecord.delete();

    console.log("[layout] DONE — boundary + trackers + (inverters/roads) landed");
  } catch (e) {
    console.error("[layout] EXCEPTION CAUGHT");
    console.error("[layout]   raw:", e);
    console.error("[layout]   String(e):", String(e));
    const wasm = WebAssembly as typeof WebAssembly & {
      Exception?: new () => object;
    };
    const isWasmException =
      typeof wasm.Exception === "function" && e instanceof wasm.Exception;
    console.error(
      "[layout]   instanceof WebAssembly.Exception:",
      isWasmException,
    );
    const m = window.Module;
    if (m && typeof m.getExceptionMessage === "function") {
      try {
        console.error(
          "[layout]   Module.getExceptionMessage:",
          m.getExceptionMessage(e),
        );
      } catch (probeErr) {
        console.error("[layout]   getExceptionMessage threw:", probeErr);
      }
    }
  } finally {
    console.timeEnd("[perf] addLayoutToCanvas total");
  }
}

export interface ODAViewerProps {
  templateUrl: string;
  layout: Layout | null;
  onTrackerCountChange?: (count: number) => void;
  addMode?: boolean;
  onExitAddMode?: () => void;
  moveMode?: boolean;
  onExitMoveMode?: () => void;
  // Pulse counter: bumping the value triggers handleDeleteSelected once.
  // Lets the LayoutPage button request a delete without ODAViewer needing
  // a callback or duplicate state for "is delete pending".
  deleteRequestId?: number;
  // Manual "Regen" pulse: bumping forces app.regenAll() + Update() — a
  // fast in-place re-render (no template/viewer reload), for refreshing
  // the display after an edit when it goes stale.
  regenRequestId?: number;
  // Stage L.5: pulse counter for client-side DWG save — same pattern as
  // deleteRequestId. Bumping the value triggers handleSaveAsDwg() once.
  saveRequestId?: number;
  // Stage L.5 DIAGNOSTIC 2: pulse counter for the fresh-template save
  // test. Loads /assets/template.dwg into a NEW db (no edits applied)
  // and tries writeFile1. Isolates "writeFile1 broken" (license/SDK)
  // from "our edits broke the db state".
  freshSaveTestRequestId?: number;
  // Stage L.5: stitched into the downloaded filename
  // (layout-<runId>-<timestamp>.dwg).
  runId?: string;
}

export function ODAViewer({
  templateUrl,
  layout,
  onTrackerCountChange,
  addMode,
  onExitAddMode,
  moveMode,
  onExitMoveMode,
  deleteRequestId,
  regenRequestId,
  saveRequestId,
  freshSaveTestRequestId,
  runId,
}: ODAViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const appRef = useRef<OdaApp | null>(null);
  const rafRef = useRef<number>(0);
  // Render-on-demand: the rAF loop only repaints when this is true (set by
  // any camera/edit interaction). ``settleUntilRef`` forces continuous
  // repaint for a short window right after load so the bulk-appended scene
  // is fully drawn before we go idle.
  const needsRenderRef = useRef(false);
  const settleUntilRef = useRef(0);
  const initOnceRef = useRef(false);
  // Click-vs-drag tracking: LMB mousedown records this, LMB mouseup
  // measures distance from it.
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);
  // CAD-style multi-select SET. Each left-click toggles a tracker in or
  // out; clicking empty space clears all. Selected trackers are shown by
  // a COLOR change (setColorIndex) — NOT entity.highlight(), which needs
  // the custom-highlighting MRT pipeline that freezes this WebGL context.
  // Map: WASM handle string → its OdaObjectId wrapper (kept open so we
  // can recolor / erase / move it without re-resolving).
  const selectedIdsRef = useRef<Map<string, OdaObjectId>>(new Map());
  // Stage L.4.d: WASM-handle-string → server tracker id map. Populated
  // during the bulk-load (one entry per tracker) and on add (after the
  // server returns the real id). Mutated on delete (drop entry). Used
  // by move/delete handlers to resolve the selected entity to a server
  // id for the POST /ops payload.
  const idMapRef = useRef<Map<string, string>>(new Map());
  // Stage L.4.d: persistence mutations + React Query client. Both held
  // in refs so the handler closures see the latest values without
  // forcing the heavy init useEffect to re-run on every render.
  const editOps = useEditOps(runId);
  const editOpsRef = useRef(editOps);
  useEffect(() => {
    editOpsRef.current = editOps;
  });
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  useEffect(() => {
    queryClientRef.current = queryClient;
  });
  // Stage 10: placement offset is now a map keyed by block name
  // (108_INT, 81_EPN, INV_PAD). Stage-9 single-block runs only
  // populate 108_INT — coords.ts ``getPlacementOffset`` falls back
  // to that entry when a specific block name isn't listed.
  const placementOffsetRef = useRef<Record<string, { x: number; y: number }>>({
    "108_INT": { x: PLACEMENT_OFFSET_X, y: PLACEMENT_OFFSET_Y },
  });
  useEffect(() => {
    if (layout) placementOffsetRef.current = layout.placement_offset;
  }, [layout]);
  // Held in a ref so callback identity changes from the parent don't
  // re-trigger the heavy init useEffect.
  const onTrackerCountChangeRef = useRef<
    ((count: number) => void) | undefined
  >(onTrackerCountChange);
  useEffect(() => {
    onTrackerCountChangeRef.current = onTrackerCountChange;
  }, [onTrackerCountChange]);
  // Stress-test instrumentation: rAF frame counter, logs avg FPS every ~3s.
  // Add-mode plumbing: ref-stashed so the canvas click closure sees the
  // current value without re-running the heavy init useEffect on prop change.
  const addModeRef = useRef<boolean>(!!addMode);
  useEffect(() => {
    addModeRef.current = !!addMode;
  }, [addMode]);
  const onExitAddModeRef = useRef<(() => void) | undefined>(onExitAddMode);
  useEffect(() => {
    onExitAddModeRef.current = onExitAddMode;
  }, [onExitAddMode]);
  // Move-mode plumbing — same ref-stash pattern as add mode.
  const moveModeRef = useRef<boolean>(!!moveMode);
  // CAD-style MOVE is a two-click gesture: the first click sets the base
  // (grab) point, the second sets the destination, and the block
  // translates by (destination − base). ``moveBasePointRef`` holds the
  // base point between the two clicks (null = waiting for the base).
  const moveBasePointRef = useRef<{ x: number; y: number } | null>(null);
  // On-canvas prompt for the current MOVE step ("Pick base point" /
  // "Pick destination point"). Empty when not moving.
  const [moveHint, setMoveHint] = useState<string>("");
  useEffect(() => {
    moveModeRef.current = !!moveMode;
    // Reset the gesture whenever move mode toggles, and drive the prompt.
    moveBasePointRef.current = null;
    setMoveHint(moveMode ? "Pick base point" : "");
  }, [moveMode]);
  const onExitMoveModeRef = useRef<(() => void) | undefined>(onExitMoveMode);
  useEffect(() => {
    onExitMoveModeRef.current = onExitMoveMode;
  }, [onExitMoveMode]);
  // Canvas cursor: 'move' beats 'crosshair' beats default. LayoutPage
  // enforces that addMode and moveMode are mutually exclusive, so the
  // priority here is just defensive.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.style.cursor = moveMode ? "move" : addMode ? "crosshair" : "";
  }, [addMode, moveMode]);
  // Delete pulse: LayoutPage bumps deleteRequestId; we fire the existing
  // handler once per new value. lastDeleteRequestIdRef guards against
  // re-fires from React StrictMode double-effect or unrelated renders.
  const lastDeleteRequestIdRef = useRef(0);
  useEffect(() => {
    if (!deleteRequestId) return;
    if (deleteRequestId === lastDeleteRequestIdRef.current) return;
    lastDeleteRequestIdRef.current = deleteRequestId;
    handleDeleteSelected();
    // handleDeleteSelected is stable closure over refs; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRequestId]);

  // Manual Regen pulse: force a full in-place re-render (regenAll +
  // Update). No template/db/viewer reload — just rebuild the graphics
  // cache from the current database and repaint. ~under a second.
  const lastRegenRequestIdRef = useRef(0);
  useEffect(() => {
    if (!regenRequestId) return;
    if (regenRequestId === lastRegenRequestIdRef.current) return;
    lastRegenRequestIdRef.current = regenRequestId;
    const app = appRef.current;
    if (!app) return;
    try {
      console.log("[regen] manual regenAll + Update");
      app.regenAll();
      app.Update();
      console.log("[regen] done");
    } catch (e) {
      console.warn("[regen] threw:", e);
    }
  }, [regenRequestId]);

  // Stage L.5: runId stashed in a ref so the save handler can stitch it
  // into the downloaded filename without re-running on every parent render.
  const runIdRef = useRef<string | null>(runId ?? null);
  useEffect(() => {
    runIdRef.current = runId ?? null;
  }, [runId]);

  // Stage L.5: save pulse — mirrors deleteRequestId. LayoutPage bumps
  // saveRequestId; we fire handleSaveAsDwg() once per new non-zero value.
  const lastSaveRequestIdRef = useRef(0);
  useEffect(() => {
    if (!saveRequestId) return;
    if (saveRequestId === lastSaveRequestIdRef.current) return;
    lastSaveRequestIdRef.current = saveRequestId;
    handleSaveAsDwg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestId]);

  // Stage L.5 DIAGNOSTIC 2: fresh-template save pulse. Same pattern.
  const lastFreshSaveTestRequestIdRef = useRef(0);
  useEffect(() => {
    if (!freshSaveTestRequestId) return;
    if (freshSaveTestRequestId === lastFreshSaveTestRequestIdRef.current)
      return;
    lastFreshSaveTestRequestIdRef.current = freshSaveTestRequestId;
    handleTestSaveFreshTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshSaveTestRequestId]);

  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [errorMsg, setErrorMsg] = useState("");
  // Narrates what the loader is doing, phase by phase, with live counts.
  const [loadingMsg, setLoadingMsg] = useState("Starting 3D engine…");

  // CRITICAL: canvas must be bound to Module BEFORE postRun fires.
  // useEffect runs too late on slow first-mounts where WASM finishes
  // faster than React schedules effects. useLayoutEffect is synchronous
  // after layout commit.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.Module) {
      window.Module.canvas = canvas;
    }
  }, []);

  // ── Bootstrap + load template + render loop (runs once) ─────────────
  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;

    waitForModuleReady()
      .then(async (mod) => {
        // Re-bind in case calledRun was true before useLayoutEffect.
        mod.canvas = canvas;

        const app = new mod.App();
        app.createDevice();
        setupDevice(app, mod);
        // IMPORTANT: do NOT enable custom highlighting. It switches the
        // renderer to a multi-render-target (MRT) pipeline, and this
        // DrawingWeb WebGL context can't satisfy the MRT draw — every
        // frame throws ``drawBuffersWEBGL: INVALID_OPERATION
        // (COLOR_ATTACHMENTi_EXT or NONE)`` and the canvas freezes
        // (blank/stale) after any repaint. We indicate selection with a
        // COLOR change (setColorIndex) instead of entity.highlight(),
        // which needs no extra render target. (Previously this called
        // app.enableCustomHighlighting(true) — that was the freeze.)

        // Per-phase try/catch for Stage-9 debugging. Inner catch logs
        // and doesn't re-throw, so the outer .catch won't fire on init
        // failures — the console story is the source of truth for now.
        try {
          // Cache-bust both the browser HTTP layer (no-store + a
          // unique query token) and the WASM in-memory FS so a
          // template edited in CAD shows up on the next hard reload.
          // Without this the singleton Module persists template bytes
          // across React remounts and stale block tables linger.
          setLoadingMsg("Loading drawing template…");
          const bustedUrl = templateUrl + (templateUrl.includes("?") ? "&" : "?") + "t=" + Date.now();
          console.log("[init] phase A: fetch template.dwg (cache-busted:", bustedUrl, ")");
          const resp = await fetch(bustedUrl, { cache: "no-store" });
          if (!resp.ok) {
            throw new Error(
              `Template fetch failed: ${resp.status} ${resp.statusText}`,
            );
          }
          console.log("[init] phase B: read arrayBuffer");
          const bytes = new Uint8Array(await resp.arrayBuffer());
          console.log("[init] phase B+: template bytes =", bytes.length);

          // (preRun in index.html already mkdir'd /assets; analyzePath
          // guards against repeat-create within the same WASM FS lifetime.)
          try {
            window.FS?.mkdir(ASSETS);
          } catch {
            // already exists
          }
          console.log("[init] phase C: FS_createDataFile (force overwrite)");
          // ALWAYS overwrite — the WASM Module is a page singleton, so
          // a stale /assets/template.dwg from a previous mount must be
          // unlinked before we write the fresh bytes. Otherwise the
          // ODA database opens the old file and never sees a template
          // edit you saved between page reloads.
          const fs = window.FS;
          if (fs && fs.analyzePath(TEMPLATE_PATH).exists) {
            try {
              // ``unlink`` is marked optional on OdaGlobalFS because some
              // SDK builds don't expose it. The current DrawingWeb build
              // does; guard anyway and fall back to FS_unlink on Module.
              const unlinkFn =
                fs.unlink ?? mod.FS_unlink;
              if (unlinkFn) unlinkFn(TEMPLATE_PATH);
            } catch (e) {
              console.warn("[init] phase C: unlink failed (continuing anyway):", e);
            }
          }
          mod.FS_createDataFile(
            ASSETS,
            TEMPLATE_NAME,
            bytes,
            true,
            true,
            true,
          );

          setLoadingMsg("Opening drawing…");
          console.log("[init] phase D: app.OpenFile");
          app.OpenFile(TEMPLATE_PATH);

          if (layout) {
            setLoadingMsg(describeLayout(layout));
            console.log("[init] phase E: addLayoutToCanvas");
            idMapRef.current.clear();
            addLayoutToCanvas(app, mod, layout, (handleStr, trackerId) => {
              idMapRef.current.set(handleStr, trackerId);
            });
            console.log(
              "[init] phase E: idMap populated with",
              idMapRef.current.size,
              "entries",
            );
          } else {
            console.log(
              "[init] phase E: no layout payload, skipping addLayoutToCanvas",
            );
          }

          setLoadingMsg("Rendering layout…");
          canvas.width = canvas.clientWidth || 800;
          canvas.height = canvas.clientHeight || 600;
          console.log(
            "[init] phase F: app.Resize",
            canvas.width,
            canvas.height,
          );
          app.Resize(canvas.width, canvas.height);

          console.log("[init] phase G: app.ZoomExtents");
          app.ZoomExtents();

          console.log("[init] phase H: app.Update");
          app.Update();

          // Stage K: defer the initial recount onto the next animation
          // frame. app.Update() alone doesn't settle the iterator for
          // multi-entity appends (single-entity erase + Update works,
          // but the bulk BTR open-write → append-many → close pattern
          // needs at least one rAF tick to commit the transactions so
          // the iterator can see them).
          console.log(
            "[init] phase H+: queuing deferred initial recount via requestAnimationFrame",
          );
          requestAnimationFrame(() => {
            console.log("[init] phase H+: deferred recount firing now");
            recountAndNotify();
          });

          console.log("[init] phase I: appRef.current = app, starting rAF");
          appRef.current = app;

          // Render continuously (requestAnimationFrame → Update every
          // frame), the ODA inWeb example cadence. A manual "Regen"
          // button (regenRequestId) lets the user force a regenAll when
          // the display goes stale after an edit.
          const loop = () => {
            rafRef.current = requestAnimationFrame(loop);
            const a = appRef.current;
            if (a) a.Update();
          };
          loop();

          setStatus("ready");
        } catch (initErr) {
          console.error("[init] EXCEPTION CAUGHT:", initErr);
          console.error("[init]   String(e):", String(initErr));
          const wasm = WebAssembly as typeof WebAssembly & {
            Exception?: new () => object;
          };
          const isWasmException =
            typeof wasm.Exception === "function" &&
            initErr instanceof wasm.Exception;
          console.error(
            "[init]   instanceof WebAssembly.Exception:",
            isWasmException,
          );
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        setStatus("error");
        setErrorMsg(msg);
      });

    return () => {
      cancelAnimationFrame(rafRef.current);
      appRef.current = null;
      // Do NOT call app.delete() — App is not Embind-disposable.
    };
  }, [templateUrl, layout]);

  // ── Kill the render loop when the tab is closed / hidden / navigated
  //    away via bfcache. The init effect's unmount cleanup already stops
  //    the loop on in-app navigation; this covers tab close + page
  //    teardown so a heavy or wedged WASM session can't keep running. ──
  useEffect(() => {
    const kill = () => {
      cancelAnimationFrame(rafRef.current);
      appRef.current = null;
    };
    window.addEventListener("pagehide", kill);
    return () => window.removeEventListener("pagehide", kill);
  }, []);

  // ── ResizeObserver: keep the WASM canvas in sync with the DOM size ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
      appRef.current?.Resize(canvas.width, canvas.height);
      needsRenderRef.current = true;
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  // ── Mouse / wheel: pan (LMB-drag), orbit (RMB-drag), zoom (wheel),
  //    extents (MMB), pick (LMB without drag) ───────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      appRef.current?.Zoom(-ev.deltaY * 0.01, ev.offsetX, ev.offsetY);
      needsRenderRef.current = true;
    };
    const onMouseDown = (ev: MouseEvent) => {
      if (ev.button === 0) {
        // record start; mouseup will decide click-vs-drag
        clickStartRef.current = { x: ev.clientX, y: ev.clientY };
      } else if (ev.buttons === 4) {
        appRef.current?.ZoomExtents();
        needsRenderRef.current = true;
      }
    };
    const onMouseMove = (ev: MouseEvent) => {
      const app = appRef.current;
      if (!app) return;
      // Default navigation: left-drag pans (Dolly), right-drag orbits.
      switch (ev.buttons) {
        case 1:
          app.Dolly(ev.movementX, ev.movementY);
          needsRenderRef.current = true;
          break;
        case 2:
          app.Orbit(ev.movementX, ev.movementY);
          needsRenderRef.current = true;
          break;
      }
    };
    const onMouseUp = (ev: MouseEvent) => {
      if (ev.button !== 0) return;
      const start = clickStartRef.current;
      clickStartRef.current = null;
      if (!start) return;
      const dx = ev.clientX - start.x;
      const dy = ev.clientY - start.y;
      if (dx * dx + dy * dy > CLICK_DRAG_PX * CLICK_DRAG_PX) return; // was a drag
      handleCanvasPick(ev);
    };
    const onContextMenu = (ev: MouseEvent) => ev.preventDefault();

    // Stage I: Delete/Backspace erases the currently-selected entity.
    // Window-level so it fires regardless of canvas focus, but skipped
    // while user is typing in an input/textarea/contenteditable.
    const onKeyDown = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }

      // ESC exits the active mode (move takes priority over add since
      // they're mutually exclusive but the check order is defensive).
      if (ev.key === "Escape") {
        ev.preventDefault();
        if (moveModeRef.current && onExitMoveModeRef.current) {
          console.log("[move] ESC pressed, exiting move mode");
          onExitMoveModeRef.current();
        } else if (addModeRef.current && onExitAddModeRef.current) {
          console.log("[add] ESC pressed, exiting add mode");
          onExitAddModeRef.current();
        }
        return;
      }
      // Keyboard delete is disabled — tracker add/delete/move are turned
      // off in the viewer (they froze the canvas on this SDK build). The
      // handlers remain in the file but have no UI/key entry point.
    };

    // WebGL context loss surfaces as a silent frozen canvas. The ODA
    // examples handle it explicitly: stop rendering and tell the user to
    // reload. We flip to the error state (which renders a "Reload viewer"
    // button) instead of leaving a dead black canvas.
    const onContextLost = (ev: Event) => {
      ev.preventDefault(); // required so a restore could fire
      console.error("[ODAViewer] WebGL context lost — viewer must reload");
      appRef.current = null; // stop the render loop from touching a dead ctx
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setErrorMsg("The 3D engine lost its GPU context. Reload to continue.");
      setStatus("error");
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("contextmenu", onContextMenu);
    canvas.addEventListener("webglcontextlost", onContextLost, false);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("contextmenu", onContextMenu);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Relocate the currently-selected entity to the given world point.
  // Block-reference-only: if the selection is the boundary polyline (or
  // anything that isn't OdDbBlockReference), the cast returns null and
  // the handler bails — boundaries aren't moved from this UI.
  // No recount (entity count unchanged); selection stays intact.
  //
  // Stage L.4.d additions: resolve the selected handle → server id via
  // idMapRef, snapshot the original position for WASM rollback, POST
  // /ops, and on failure setPosition back to the original.
  // CAD-style MOVE: translate the selected block by the displacement
  // vector (dx, dy) — the delta between the user's destination click and
  // the base point they picked first. The block keeps its grab offset
  // (unlike the old "snap insertion point to the cursor" behaviour); it
  // simply shifts by the vector, exactly like AutoCAD's MOVE command.
  async function handleMoveSelectedByDelta(
    dx: number,
    dy: number,
  ): Promise<void> {
    const app = appRef.current;
    const mod = window.Module;
    if (!app || !mod) return;
    const sel = selectedIdsRef.current;
    if (sel.size === 0) {
      console.log("[move] nothing selected, no-op");
      return;
    }
    console.log(`[move] translating ${sel.size} tracker(s) by`, { dx, dy });

    for (const [handleStr, selId] of sel) {
      const trackerId = idMapRef.current.get(handleStr);
      if (!trackerId) {
        console.warn("[move] handle not in idMap, skipping", handleStr);
        continue;
      }

      let originalX = 0;
      let originalY = 0;
      let newX = 0;
      let newY = 0;
      try {
        console.log("[move] phase 1: open selected entity for write");
        const pObj = selId.safeOpenObject(
          window.OpenMode.kForWrite,
          false,
        );

        console.log("[move] phase 2: cast to OdDbBlockReference");
        const pRef = mod.OdDbBlockReference.cast(pObj);
        pObj.delete();

        if (!pRef) {
          console.warn("[move] not a block reference, skipping", trackerId);
          continue;
        }

      // Snapshot the original insertion point BEFORE mutating, so we
      // can restore it if the server rejects the move.
      const origPos = pRef.position();
      originalX = origPos.x;
      originalY = origPos.y;
      origPos.delete();

      // Translate by the displacement vector (CAD MOVE): new insertion =
      // original insertion + (dx, dy).
      newX = originalX + dx;
      newY = originalY + dy;
      console.log(
        "[move] phase 3: translate by",
        { dx, dy },
        "→ insertion",
        { newX, newY },
      );
      // No highlight to toggle — selection is a colour change, which
      // simply travels with the block. Just set the new position; the
      // tracker stays selected (coloured) at its new location.
      const pos = new mod.OdGePoint3d(newX, newY, 0);
      pRef.setPosition(pos);
      pos.delete();

      console.log("[move] phase 4: cleanup wrapper");
      pRef.delete();

      console.log("[move] DONE — entity translated to", { newX, newY });
    } catch (e) {
      console.error("[move] EXCEPTION CAUGHT");
      console.error("[move]   raw:", e);
      console.error("[move]   String(e):", String(e));
      const wasm = WebAssembly as typeof WebAssembly & {
        Exception?: new () => object;
      };
      const isWasmException =
        typeof wasm.Exception === "function" && e instanceof wasm.Exception;
      console.error(
        "[move]   instanceof WebAssembly.Exception:",
        isWasmException,
      );
      const m = window.Module;
      if (m && typeof m.getExceptionMessage === "function") {
        try {
          console.error(
            "[move]   Module.getExceptionMessage:",
            m.getExceptionMessage(e),
          );
        } catch (probeErr) {
          console.error("[move]   getExceptionMessage threw:", probeErr);
        }
      }
      continue;
    }

    // Refresh SYNCHRONOUSLY right after the move, before the async POST
    // (statemachine.js ordering). Deferring past the `await` lets the
    // continuous rAF loop paint the moved entity's stale graphics cache
    // (no preceding invalidate) during the round-trip → frozen canvas.
    try {
      const dev = app.getDevice();
      dev.invalidate();
      app.Update();
    } catch (refreshErr) {
      console.warn("[move] sync refresh after move threw:", refreshErr);
    }

    // ── Persist this tracker's move (allowed on every layout incl.
    // AC-block runs; apply_op preserves block / block_id). ───────────
    const { sw_x, sw_y } = insertionToSw(
      { x: newX, y: newY },
      placementOffsetRef.current,
    );
    console.log(
      "[move] phase 6: POST /ops move_tracker",
      { id: trackerId, sw_x, sw_y },
    );

    try {
      await editOpsRef.current.moveTracker.mutateAsync({
        id: trackerId,
        sw_x,
        sw_y,
      });
      console.log("[move] phase 7: server accepted move", trackerId);
    } catch (err) {
      console.error("[move] phase 7: server failed, rolling back WASM:", err);
      // Restore the original position in WASM. The hook's onError
      // already restored the cache + toasted.
      try {
        const pErr = selId.safeOpenObject(window.OpenMode.kForWrite, false);
        const pRefErr = mod.OdDbBlockReference.cast(pErr);
        pErr.delete();
        if (pRefErr) {
          const origRestore = new mod.OdGePoint3d(originalX, originalY, 0);
          pRefErr.setPosition(origRestore);
          origRestore.delete();
          pRefErr.delete();
        }
      } catch (rollbackErr) {
        console.error("[move] rollback setPosition threw:", rollbackErr);
      }
    }
    } // end for-loop over selected trackers

    requestRepaint();
  }

  // Drop a single 108_INT block reference at the given world point.
  // Mirrors the per-tracker block in addLayoutToCanvas: open model space
  // for write → look up block ID → createObject → setDatabaseDefaults →
  // setLayer → appendOdDbEntity (BEFORE setBlockTableRecord per example
  // 03) → setBlockTableRecord → setPosition. Click point IS the
  // insertion point (no SW-corner offset) per Stage J spec.
  //
  // Stage L.4.d additions: register the new block-ref's handle in the
  // idMap with a tempId, POST /ops on the server, swap to the real
  // ``t-NNNN`` on success or undo (erase the block-ref, drop the idMap
  // entry) on failure. The hook handles the optimistic cache update +
  // rollback + toast.
  async function handleAddTrackerAtPoint(worldPt: OdaPoint3d): Promise<void> {
    const app = appRef.current;
    const mod = window.Module;
    if (!app || !mod) return;

    let handleStr: string | null = null;
    let createdObjId: OdaObjectId | null = null;
    try {
      console.log("[add] phase 1: open model space for write");
      const db = app.getDb();
      const modelSpaceId = db.getModelSpaceId();
      const pObj = modelSpaceId.safeOpenObject(
        window.OpenMode.kForWrite,
        false,
      );
      const pRecord = mod.OdDbBlockTableRecord.cast(pObj);
      pObj.delete();

      console.log("[add] phase 2: look up 108_INT block");
      const blockTableId = db.getBlockTableId();
      const pBtObj = blockTableId.safeOpenObject(
        window.OpenMode.kForRead,
        false,
      );
      const pBlockTable = mod.OdDbBlockTable.cast(pBtObj);
      pBtObj.delete();
      const blockName = new mod.OdString(TRACKER_BLOCK_NAME);
      const blockId = pBlockTable.getAt2(blockName, false);
      blockName.delete();
      pBlockTable.delete();

      if (blockId.isNull()) {
        console.error(
          `[add] block "${TRACKER_BLOCK_NAME}" not found, aborting`,
        );
        pRecord.delete();
        return;
      }

      console.log(
        "[add] phase 3: create OdDbBlockReference at world",
        worldPt.x,
        worldPt.y,
      );
      const pRef = mod.OdDbBlockReference.createObject();
      pRef.setDatabaseDefaults(db, false);
      const layerName = new mod.OdString(TRACKERS_LAYER);
      pRef.setLayer(layerName, false, false);
      layerName.delete();
      // CRITICAL (example 03): append BEFORE setBlockTableRecord.
      createdObjId = pRecord.appendOdDbEntity(pRef);
      pRef.setBlockTableRecord(blockId);
      const pos = new mod.OdGePoint3d(worldPt.x, worldPt.y, 0);
      pRef.setPosition(pos);
      pos.delete();
      pRef.delete();
      pRecord.delete();

      // Capture the new entity's handle string for the idMap.
      handleStr = handleStringFor(createdObjId);

      console.log("[add] phase 4: sync refresh + deferred recount");
      // Sync refresh right after the append, before the async POST below
      // (statemachine.js ordering) so the rAF loop never paints the new
      // entity's stale graphics cache during the round-trip → no freeze.
      try {
        const dev = app.getDevice();
        dev.invalidate();
        app.Update();
      } catch (refreshErr) {
        console.warn("[add] sync refresh after append threw:", refreshErr);
      }
      // Defer recount one frame so the iterator sees the new entity —
      // same Stage K lesson: Update alone doesn't settle multi-entity
      // ops, and even a single append needs a frame to be visible.
      requestAnimationFrame(() => {
        recountAndNotify();
      });

      console.log("[add] DONE — tracker landed at", worldPt.x, worldPt.y);
    } catch (e) {
      console.error("[add] EXCEPTION CAUGHT");
      console.error("[add]   raw:", e);
      console.error("[add]   String(e):", String(e));
      const wasm = WebAssembly as typeof WebAssembly & {
        Exception?: new () => object;
      };
      const isWasmException =
        typeof wasm.Exception === "function" && e instanceof wasm.Exception;
      console.error(
        "[add]   instanceof WebAssembly.Exception:",
        isWasmException,
      );
      const m = window.Module;
      if (m && typeof m.getExceptionMessage === "function") {
        try {
          console.error(
            "[add]   Module.getExceptionMessage:",
            m.getExceptionMessage(e),
          );
        } catch (probeErr) {
          console.error("[add]   getExceptionMessage threw:", probeErr);
        }
      }
      if (createdObjId) {
        try {
          createdObjId.delete();
        } catch {
          /* ignore */
        }
      }
      return;
    }

    // ── Stage L.4.d: persistence + idMap finalisation ────────────────
    if (!handleStr) {
      console.warn(
        "[add] no handleStr captured — entity was created but cannot be persisted (idMap entry missing).",
      );
      try {
        createdObjId?.delete();
      } catch {
        /* ignore */
      }
      return;
    }

    // Add is allowed on every layout, including AC-block runs. The new
    // tracker is a plain 108_INT block-ref with no ``block_id`` (an
    // orphan, greyed by the renderer); ``apply_op`` stores it as a
    // ``t-NNNN`` record and the renderer defaults its missing ``block``
    // to 108_INT on reload.
    const tempId = `temp-${crypto.randomUUID()}`;
    idMapRef.current.set(handleStr, tempId);
    const { sw_x, sw_y } = insertionToSw(
      { x: worldPt.x, y: worldPt.y },
      placementOffsetRef.current,
    );
    console.log(
      "[add] phase 5: POST /ops add_tracker",
      { tempId, sw_x, sw_y },
    );

    try {
      const real = await editOpsRef.current.addTracker.mutateAsync({
        tempId,
        sw_x,
        sw_y,
      });
      idMapRef.current.set(handleStr, real.id);
      console.log("[add] phase 6: server returned real id", real.id);
    } catch (err) {
      console.error("[add] phase 6: server failed, rolling back WASM:", err);
      idMapRef.current.delete(handleStr);
      // Re-open and erase the block-ref we just created.
      if (createdObjId) {
        try {
          const pErr = createdObjId.safeOpenObject(
            window.OpenMode.kForWrite,
            false,
          );
          const ent = mod.OdDbEntity.cast(pErr);
          pErr.delete();
          if (ent) {
            ent.erase(true);
            ent.delete();
            requestRepaint();
            recountAndNotify();
          }
        } catch (eraseErr) {
          console.error("[add] rollback erase threw:", eraseErr);
        }
      }
    } finally {
      try {
        createdObjId?.delete();
      } catch {
        /* ignore */
      }
    }
  }

  // Recount trackers in model space and notify the parent. Called after
  // every mutation site (initial bulk-load, delete, add). Reads app/mod
  // from refs/window so it stays callable from any handler.
  //
  // Stage L.4.d: cache-derived UI counter doesn't need this, so we
  // short-circuit when no callback is wired. recountTrackers iterates
  // the entire model space (~30ms on a 1380-tracker scene); skipping
  // it when nobody listens is pure win.
  // Request a repaint through the rAF render loop instead of calling
  // app.Update() synchronously from an event handler. A synchronous
  // Update draws OUTSIDE the animation frame, which leaves the ODA
  // WebGL renderer's multi-render-target framebuffers in a half-bound
  // state — the source of the repeated
  // ``drawBuffersWEBGL: INVALID_OPERATION (COLOR_ATTACHMENTi_EXT or NONE)``
  // errors and the post-edit canvas freeze. Flipping ``needsRenderRef``
  // and opening a short settle window lets the loop redraw several
  // consecutive in-frame passes so the renderer re-stabilises (same
  // mechanism the initial load uses).
  function requestRepaint(settleMs = 800): void {
    // After a DB edit, force a FULL device redraw. The ODA WebGL device
    // does PARTIAL (incremental) updates by default — only the changed
    // region is recomposited, through an MRT path (glDrawBuffers) that
    // this WebGL context can't satisfy (drawBuffersWEBGL INVALID_OPERATION).
    // The initial FULL render survives it, but the post-edit partial
    // update produces no output → the canvas freezes (app stays alive).
    // device.invalidate() invalidates the whole surface so the next
    // App.Update() redraws everything (the working full-render path),
    // exactly like statemachine.js (device.invalidate() → app.Update()).
    const app = appRef.current;
    if (app) {
      try {
        const device = app.getDevice();
        device.invalidate();
      } catch (e) {
        console.warn("[repaint] device.invalidate threw:", e);
      }
    }
    needsRenderRef.current = true;
    settleUntilRef.current = performance.now() + settleMs;
  }

  // Selection feedback uses entity.highlight() — the ODA selection
  // example pattern (open the member for write → highlight(true/false)).
  // It works WITHOUT custom highlighting, which we keep OFF (that MRT
  // pipeline is what froze the canvas). Recolouring the block-ref insert
  // doesn't work — the tracker outline is drawn ByLayer, so the insert's
  // colour is ignored — which is why highlight() is the right tool here.
  function setEntityHighlight(objId: OdaObjectId, on: boolean): void {
    const mod = window.Module;
    if (!mod) return;
    try {
      const o = objId.safeOpenObject(window.OpenMode.kForWrite, false);
      const ent = mod.OdDbEntity.cast(o);
      if (ent) {
        ent.highlight(on, null, true);
        ent.delete();
      }
      o.delete();
    } catch (e) {
      console.warn("[select] highlight threw:", e);
    }
  }

  // Deselect everything: unhighlight each selected tracker, free its
  // wrapper, and empty the set. Caller triggers the repaint.
  function clearSelection(): void {
    for (const [, objId] of selectedIdsRef.current) {
      setEntityHighlight(objId, false);
      objId.delete();
    }
    selectedIdsRef.current.clear();
  }

  function recountAndNotify(): void {
    if (!onTrackerCountChangeRef.current) return;
    const app = appRef.current;
    const mod = window.Module;
    if (!app || !mod) return;
    const count = recountTrackers(app, mod, TRACKER_BLOCK_NAME);
    onTrackerCountChangeRef.current(count);
  }

  // Stage L.4.d: read a stable string handle off an OdDbObjectId so we
  // can look the entity up in idMapRef. Allocates two WASM wrappers
  // (handle + ascii string) — both are .delete()d before return.
  // Returns ``null`` only if the WASM call itself fails (defensive;
  // callers can fall through to a "selection lost" path).
  function handleStringFor(objId: OdaObjectId): string | null {
    try {
      const h = objId.getHandle();
      const ascii = h.ascii();
      const s = ascii.toString();
      ascii.delete();
      h.delete();
      return s;
    } catch (err) {
      console.warn("[idmap] getHandle threw:", err);
      return null;
    }
  }

  // Delete the currently-selected entity. Follows the canonical pattern
  // from examples 08, 19, 26, 29: safeOpenObject(kForWrite) → cast to
  // OdDbEntity → close the open handle → entity.erase(true) → cleanup.
  // No regenAll (throws on this SDK); app.Update repaints next frame.
  //
  // Stage L.4.d additions: resolve handle → server id via idMap,
  // snapshot the tracker record (sw_x, sw_y) for recreate-fallback
  // rollback, POST /ops, and on failure try erase(false) un-erase
  // first then recreate the block-ref from snapshot if un-erase fails.
  // DELETE = HIDE in the viewer + delete on the backend. Your test
  // proved it: a plain regenAll() does NOT crash, but erase() does — so
  // the *erase* is what kills the WebGL context, not the render. We avoid
  // erase entirely. setVisibility(kInvisible) is a MODIFICATION (like the
  // recolor we already do on select — safe), so the tracker just vanishes
  // from the view with NO context crash, NO erase, NO full reload. The
  // backend delete makes it permanent; on the next natural reload the
  // tracker is truly gone from the data too.
  async function handleDeleteSelected(): Promise<void> {
    const app = appRef.current;
    const mod = window.Module;
    if (!app || !mod) return;
    const sel = selectedIdsRef.current;
    if (sel.size === 0) {
      console.log("[delete] nothing selected, no-op");
      return;
    }
    const entries = [...sel.entries()];
    sel.clear();

    const kInvisible =
      (window as unknown as { Visibility?: { kInvisible: number } }).Visibility
        ?.kInvisible ?? 0;

    const ids: string[] = [];
    for (const [handleStr, objId] of entries) {
      const tid = idMapRef.current.get(handleStr);
      if (!tid) {
        objId.delete();
        continue;
      }
      // HIDE (not erase) — setVisibility is a safe property modification.
      try {
        const pObj = objId.safeOpenObject(window.OpenMode.kForWrite, false);
        const pEnt = mod.OdDbEntity.cast(pObj);
        pObj.delete();
        if (pEnt) {
          pEnt.highlight(false, null, true);
          pEnt.setVisibility(kInvisible);
          pEnt.delete();
          console.log("[delete] hid tracker (setVisibility kInvisible)", tid);
        }
      } catch (e) {
        console.warn("[delete] setVisibility threw for", tid, e);
      }
      idMapRef.current.delete(handleStr);
      ids.push(tid);
      objId.delete();
    }

    // Repaint so the hidden trackers vanish (no regen, no reload).
    app.Update();

    if (ids.length === 0) {
      console.warn("[delete] no server ids for selection — nothing to delete");
      return;
    }

    // Persist on the backend — the data is the source of truth.
    console.log(`[delete] deleting ${ids.length} tracker(s) on the BACKEND`);
    for (const tid of ids) {
      try {
        await editOpsRef.current.deleteTracker.mutateAsync({ id: tid });
        console.log("[delete] backend accepted", tid);
      } catch (err) {
        console.error("[delete] backend delete failed for", tid, err);
      }
    }
    recountAndNotify();
  }

  // Stage L.5: serialize the live in-memory database to a .dwg and
  // download it.
  //
  // The flow is a deliberate two-step round-trip through the virtual FS:
  //
  //   (a) db.writeFile1(odPath, kDwg, kDHL_CURRENT, false, 16)
  //         — writes the DWG to a path in the Emscripten VFS. Confirmed
  //           working (example 29's canonical save path).
  //   (b) services.createStreamBuf(odPath)
  //         — opens the VFS path as a readable streambuf.
  //   (c) drain via getByte() (or bulk method if Embind exposes one).
  //
  // Dead ends ruled out:
  //   • FS.readFile / FS_readFile / _FS — this SDK build has a write-only
  //     FS surface (only FS_createDataFile/createLazyFile/preloadFile).
  //     Can't read VFS bytes back to JS through the Emscripten FS API.
  //   • db.writeFile(streambuf, ...) — d.ts:4548 overload. Returns in
  //     0 ms against a services.createStreamBuf result, leaving the
  //     buffer empty (the path-backed stream is opened read-only).
  //   • OdStreamBuf.createObject() — throws WebAssembly.Exception. The
  //     class is an abstract base; Embind can't instantiate it.
  //
  // Read strategy on the readable streambuf:
  //   PATH 1 — bulk read if a method like getBytes/read/copyTo is
  //            present on the streambuf prototype (Embind sometimes
  //            adds convenience accessors not in the d.ts).
  //   PATH 2 — byte-by-byte getByte() loop (always works; slower,
  //            ~1-3s for a 500KB-2MB DWG).
  //
  // length() returns int64 → JS BigInt. Convert with Number(...) before
  // passing to Uint8Array or a Number loop counter.
  //
  // App owns the db — do NOT db.delete(). No regenAll needed (writeFile1
  // works off the database, not the graphics system).
  function handleSaveAsDwg(): void {
    const app = appRef.current;
    const mod = window.Module;
    if (!app || !mod) {
      console.warn("[save] app or Module missing, aborting");
      return;
    }

    // Phase 0: SDK surface probe — one compact log line. Documents the
    // two Embind constraints (SaveType/DwgVersion namespaces flattened
    // out; OdStreamBuf.createObject throws — abstract base) without
    // ten separate console.log calls.
    const modAsRecord = mod as unknown as Record<string, unknown>;
    const winFS = window.FS;
    const fsRecord = winFS as unknown as Record<string, unknown> | undefined;
    console.log("[save] phase 0: surface probe", {
      OdStreamBuf: typeof mod.OdStreamBuf,
      Services: typeof mod.Services,
      SaveType: typeof mod.SaveType,
      DwgVersion: typeof mod.DwgVersion,
      "window.FS": typeof winFS,
      "FS.readFile": fsRecord && typeof fsRecord.readFile,
      "FS.stat": fsRecord && typeof fsRecord.stat,
      "FS.unlink": fsRecord && typeof fsRecord.unlink,
      "FS.analyzePath": fsRecord && typeof fsRecord.analyzePath,
    });

    if (!mod.Services) {
      const allKeys = Object.keys(modAsRecord)
        .filter((k) => /stream|buffer|services/i.test(k))
        .sort();
      console.error(
        "[save] Services missing on Module — cannot construct streambuf. " +
          "Aborting. Candidates on Module matching /stream|buffer|services/i:",
        allKeys,
      );
      return;
    }

    // SaveType.kDwg = 0 and DwgVersion.kDHL_CURRENT = 33 per d.ts:7866,
    // d.ts:11001. The Embind build doesn't expose mod.SaveType or
    // mod.DwgVersion as namespace objects (confirmed via phase-0 probe);
    // passing the underlying ints directly works because the C++
    // signature takes int parameters.
    const SAVE_TYPE_DWG = 0;
    const DWG_VERSION_CURRENT = 33; // AC1032 = AutoCAD 2018

    const VFS_PATH = "/export.dwg";
    let sb: OdaStreamBuf | null = null;
    let services: OdaServices | null = null;
    let pName: OdaString | null = null;
    try {
      // Phase 1: instantiate Services for the readback step. We don't
      // build a streambuf yet — db.writeFile(streambuf, ...) returns a
      // no-op against the path-backed read-only stream that
      // createStreamBuf produces (testing showed phase-4 timing of
      // 0.0ms and an empty 0-length buffer). Instead we write through
      // writeFile1 to the VFS path first, then re-open that path as a
      // readable streambuf to drain the bytes back to JS.
      console.log("[save] phase 1: new Services + initialize");
      services = new mod.Services();
      services.initialize();

      // Phase 2: get db (do NOT delete — App owns it).
      console.log("[save] phase 2: get db");
      const db = app.getDb();

      // ============================================================
      // DIAG-OFFICIAL: try the official "Saving a Database" pattern from
      //   cloud.opendesign.com/docs/index.html#/pages/drawing/introduction/
      //   SavingDatabase
      // The official sample passes a PLAIN JS STRING (not OdString) and
      // calls .writeFile (not writeFile1). Hypothesis: Embind auto-marshals
      // JS string → OdString correctly, but our manually-constructed
      // `new mod.OdString(path)` may produce a handle the filer can't use,
      // which would explain writeFile1 returning in 0.1ms with a 0-byte
      // result. Diagnostic-only — no download. Each variant writes to a
      // unique /diag-*.dwg so we can compare sizes.
      // ============================================================
      const dbAny = db as unknown as Record<string, unknown>;
      console.log("[save] DIAG-OFFICIAL: method types", {
        "db.writeFile": typeof dbAny.writeFile,
        "db.writeFile1": typeof dbAny.writeFile1,
        "db.save": typeof dbAny.save,
        "db.save1": typeof dbAny.save1,
      });

      const diagStat = (path: string) => {
        const info = winFS?.analyzePath(path);
        if (!info?.exists) return "(missing)";
        if (winFS?.stat) {
          try {
            return String(winFS.stat(path).size);
          } catch {
            /* ignore */
          }
        }
        return "(exists, size unknown)";
      };

      // Variant A: db.writeFile(plain-string, ...) — the official pattern.
      try {
        const path = "/diag-A-writeFile-string.dwg";
        const fn = dbAny.writeFile as
          | ((
              p: string,
              t: number,
              v: number,
              tn: boolean,
              dp: number,
            ) => void)
          | undefined;
        if (typeof fn !== "function") {
          console.log("[save] DIAG-A: db.writeFile not a function — skipped");
        } else {
          const t0 = performance.now();
          fn.call(db, path, 0, 30, false, 16); // vAC24 = 30 (AutoCAD 2010)
          const t1 = performance.now();
          console.log(
            `[save] DIAG-A: db.writeFile(plain-string, kDwg, vAC24) → size=${diagStat(path)}, time=${(t1 - t0).toFixed(1)}ms`,
          );
        }
      } catch (err) {
        console.log("[save] DIAG-A: threw:", String(err));
      }

      // Variant B: db.writeFile1(plain-string, ...) — same method we've
      // been using, but plain string instead of OdString wrapper.
      try {
        const path = "/diag-B-writeFile1-string.dwg";
        const fn = dbAny.writeFile1 as
          | ((
              p: string,
              t: number,
              v: number,
              tn: boolean,
              dp: number,
            ) => void)
          | undefined;
        if (typeof fn !== "function") {
          console.log(
            "[save] DIAG-B: db.writeFile1 not a function — skipped",
          );
        } else {
          const t0 = performance.now();
          fn.call(db, path, 0, 30, false, 16);
          const t1 = performance.now();
          console.log(
            `[save] DIAG-B: db.writeFile1(plain-string, kDwg, vAC24) → size=${diagStat(path)}, time=${(t1 - t0).toFixed(1)}ms`,
          );
        }
      } catch (err) {
        console.log("[save] DIAG-B: threw:", String(err));
      }

      // Variant C: db.writeFile1(plain-string, ...) with kDHL_CURRENT=33
      // — controls for whether vAC24 vs vCURRENT matters.
      try {
        const path = "/diag-C-writeFile1-string-v33.dwg";
        const fn = dbAny.writeFile1 as
          | ((
              p: string,
              t: number,
              v: number,
              tn: boolean,
              dp: number,
            ) => void)
          | undefined;
        if (typeof fn !== "function") {
          console.log(
            "[save] DIAG-C: db.writeFile1 not a function — skipped",
          );
        } else {
          const t0 = performance.now();
          fn.call(db, path, 0, 33, false, 16);
          const t1 = performance.now();
          console.log(
            `[save] DIAG-C: db.writeFile1(plain-string, kDwg, vCURRENT) → size=${diagStat(path)}, time=${(t1 - t0).toFixed(1)}ms`,
          );
        }
      } catch (err) {
        console.log("[save] DIAG-C: threw:", String(err));
      }

      // Variant D: db.save1(plain-string, false) — simpler signature.
      try {
        const path = "/diag-D-save1-string.dwg";
        const fn = dbAny.save1 as
          | ((p: string, tn: boolean) => void)
          | undefined;
        if (typeof fn !== "function") {
          console.log("[save] DIAG-D: db.save1 not a function — skipped");
        } else {
          const t0 = performance.now();
          fn.call(db, path, false);
          const t1 = performance.now();
          console.log(
            `[save] DIAG-D: db.save1(plain-string) → size=${diagStat(path)}, time=${(t1 - t0).toFixed(1)}ms`,
          );
        }
      } catch (err) {
        console.log("[save] DIAG-D: threw:", String(err));
      }

      console.log("[save] DIAG-OFFICIAL: end of variant tests");
      // ============================================================
      // END DIAG-OFFICIAL — production save below
      // ============================================================

      // Phase 3: build the VFS path arg (must be OdString, not string,
      // for writeFile1 — see d.ts:4549).
      console.log("[save] phase 3: construct OdString filename");
      pName = new mod.OdString(VFS_PATH);

      // Phase 4: write database to VFS. Raw int args because the
      // SaveType/DwgVersion namespace objects aren't surfaced (see
      // SAVE_TYPE_DWG / DWG_VERSION_CURRENT comment above).
      console.log(
        "[save] phase 4: db.writeFile1(",
        VFS_PATH,
        ",",
        SAVE_TYPE_DWG,
        ",",
        DWG_VERSION_CURRENT,
        ", false, 16)",
      );
      const t4 = performance.now();
      db.writeFile1(pName, SAVE_TYPE_DWG, DWG_VERSION_CURRENT, false, 16);
      console.log(
        "[save] phase 4 done in",
        (performance.now() - t4).toFixed(1),
        "ms",
      );

      // Phase 4.5: did writeFile1 actually create the VFS file? Use
      // window.FS (NOT mod.FS — they're different objects; window.FS is
      // what notation.js exposes and is the real Emscripten FS namespace
      // in this build).
      if (winFS && typeof winFS.analyzePath === "function") {
        const info = winFS.analyzePath(VFS_PATH);
        console.log(
          "[save] phase 4.5: window.FS.analyzePath(",
          VFS_PATH,
          ").exists =",
          info.exists,
        );
        if (winFS.stat && info.exists) {
          try {
            const st = winFS.stat(VFS_PATH);
            console.log("[save] phase 4.5: window.FS.stat.size =", st.size);
          } catch (statErr) {
            console.warn("[save] phase 4.5: stat threw:", String(statErr));
          }
        }
      } else {
        console.log("[save] phase 4.5: window.FS.analyzePath unavailable");
      }

      // Phase 4.6: if window.FS.readFile is available, use it directly
      // (single bulk read, fast). This is the canonical example-29 path.
      // The streambuf re-open dance below is only needed when window.FS
      // doesn't expose readFile in this build.
      if (winFS && typeof winFS.readFile === "function") {
        console.log(
          "[save] phase 4.6: window.FS.readFile available — FAST PATH",
        );
        const t46 = performance.now();
        const bytesFast = winFS.readFile(VFS_PATH);
        console.log(
          "[save] phase 4.6: read",
          bytesFast.length,
          "bytes via window.FS.readFile in",
          (performance.now() - t46).toFixed(1),
          "ms",
        );

        // Phase 6 (early): build Blob + trigger download from the fast path.
        console.log("[save] phase 6: build Blob + trigger download (fast path)");
        const blob = new Blob([bytesFast.buffer as ArrayBuffer], {
          type: "application/acad",
        });
        const url = URL.createObjectURL(blob);
        const ts = new Date()
          .toISOString()
          .replace(/[-:T]/g, "")
          .replace(/\..+$/, "")
          .replace(/(\d{8})(\d{6})/, "$1-$2");
        const filename = `layout-${runIdRef.current ?? "mock"}-${ts}.dwg`;
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        // Try to clean up the VFS file so back-to-back saves don't
        // accumulate state. Best-effort; non-fatal if unlink missing.
        if (typeof winFS.unlink === "function") {
          try {
            winFS.unlink(VFS_PATH);
          } catch (unlinkErr) {
            console.warn(
              "[save] window.FS.unlink threw (non-fatal):",
              String(unlinkErr),
            );
          }
        }
        console.log("[save] DONE — downloaded", filename, "(fast path)");
        return;
      }
      console.log(
        "[save] phase 4.6: window.FS.readFile NOT available — falling through to streambuf re-open",
      );

      // Phase 5a: re-open the VFS path as a readable streambuf.
      console.log("[save] phase 5a: services.createStreamBuf('", VFS_PATH, "')");
      sb = services.createStreamBuf(VFS_PATH);
      if (!sb) {
        throw new Error("services.createStreamBuf returned null after writeFile1");
      }

      // Probe the streambuf prototype so any Embind-added bulk read
      // method surfaces in the log automatically.
      const proto = Object.getPrototypeOf(sb) as object;
      const methods = Object.getOwnPropertyNames(proto).sort();
      console.log("[save] phase 5b: OdStreamBuf instance methods:", methods);
      const sbAny = sb as unknown as Record<string, unknown>;
      console.log(
        "[save] phase 5b: streambuf has getBytes:",
        typeof sbAny.getBytes,
      );
      console.log("[save] phase 5b: streambuf has read:", typeof sbAny.read);
      console.log(
        "[save] phase 5b: streambuf has copyTo:",
        typeof sbAny.copyTo,
      );

      // Phase 5c: drain bytes. length() returns int64 → JS BigInt;
      // convert with Number(...) for the Uint8Array constructor and
      // the loop counter. DWG files are well under 2^53 bytes so the
      // conversion is lossless.
      const lenRaw = sb.length();
      const len = typeof lenRaw === "bigint" ? Number(lenRaw) : lenRaw;
      console.log(
        "[save] phase 5c: streambuf length =",
        len,
        "(raw:",
        lenRaw,
        ")",
      );
      if (!len || len <= 0) {
        throw new Error(
          `streambuf length = ${len} after writeFile1 — VFS file empty or unreadable`,
        );
      }
      sb.rewind();

      let bytes: Uint8Array;
      let readPath: "PATH 1 (bulk)" | "PATH 2 (byte-by-byte)";
      const t5 = performance.now();
      if (typeof sbAny.getBytes === "function") {
        bytes = (sbAny.getBytes as (n: number) => Uint8Array)(len);
        readPath = "PATH 1 (bulk)";
      } else if (typeof sbAny.read === "function") {
        bytes = (sbAny.read as (n: number) => Uint8Array)(len);
        readPath = "PATH 1 (bulk)";
      } else {
        bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = sb.getByte();
        readPath = "PATH 2 (byte-by-byte)";
      }
      console.log(
        "[save] phase 5d: drained",
        bytes.length,
        "bytes via",
        readPath,
        "in",
        (performance.now() - t5).toFixed(1),
        "ms",
      );

      // Phase 6: build Blob + trigger download.
      console.log("[save] phase 6: build Blob + trigger download");
      const blob = new Blob([bytes.buffer as ArrayBuffer], {
        type: "application/acad",
      });
      const url = URL.createObjectURL(blob);

      // YYYYMMDD-HHMMSS from ISO timestamp.
      const ts = new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .replace(/\..+$/, "")
        .replace(/(\d{8})(\d{6})/, "$1-$2");
      const filename = `layout-${runIdRef.current ?? "mock"}-${ts}.dwg`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log("[save] DONE — downloaded", filename);
    } catch (e) {
      console.error("[save] EXCEPTION CAUGHT");
      console.error("[save]   raw:", e);
      console.error("[save]   String(e):", String(e));
      const wasm = WebAssembly as typeof WebAssembly & {
        Exception?: new () => object;
      };
      const isWasmException =
        typeof wasm.Exception === "function" && e instanceof wasm.Exception;
      console.error(
        "[save]   instanceof WebAssembly.Exception:",
        isWasmException,
      );
      if (typeof mod.getExceptionMessage === "function") {
        try {
          console.error(
            "[save]   Module.getExceptionMessage:",
            mod.getExceptionMessage(e),
          );
        } catch (probeErr) {
          console.error("[save]   getExceptionMessage threw:", probeErr);
        }
      }
    } finally {
      if (pName) {
        try {
          pName.delete();
        } catch (err) {
          console.warn("[save] OdString.delete threw:", err);
        }
      }
      if (sb) {
        try {
          sb.delete();
        } catch (err) {
          console.warn("[save] streambuf.delete threw:", err);
        }
      }
      if (services) {
        try {
          services.uninitialize();
        } catch (err) {
          console.warn("[save] services.uninitialize threw:", err);
        }
        try {
          services.delete?.();
        } catch (err) {
          console.warn("[save] services.delete threw:", err);
        }
      }
    }
  }

  // Stage L.5 DIAGNOSTIC 2: load /assets/template.dwg into a FRESH
  // OdDbDatabase via services.readFile (no edits applied), then attempt
  // writeFile1. If this saves correctly → our edit handlers (add/move/
  // delete) are putting the live db into an unsaveable state and we need
  // to investigate. If it ALSO produces 0 bytes → writeFile1 is broken
  // at the SDK-build / licensing level and we need ODA support.
  function handleTestSaveFreshTemplate(): void {
    const mod = window.Module;
    const winFS = window.FS;
    if (!mod) {
      console.warn("[fresh-save] Module missing, aborting");
      return;
    }
    if (!mod.Services) {
      console.warn("[fresh-save] mod.Services missing, aborting");
      return;
    }
    if (!winFS) {
      console.warn("[fresh-save] window.FS missing, aborting");
      return;
    }

    console.log("[fresh-save] === DIAGNOSTIC 2: fresh-template save test ===");

    let services: OdaServices | null = null;
    let pName: OdaString | null = null;
    try {
      console.log("[fresh-save] phase 1: new Services + initialize");
      services = new mod.Services();
      services.initialize();

      console.log(
        "[fresh-save] phase 2: services.readFile(",
        TEMPLATE_PATH,
        ")",
      );
      const tRead0 = performance.now();
      const freshDb = services.readFile(TEMPLATE_PATH);
      const tRead1 = performance.now();
      console.log(
        "[fresh-save] phase 2: readFile returned in",
        (tRead1 - tRead0).toFixed(1),
        "ms — freshDb?",
        !!freshDb,
      );
      if (!freshDb) {
        throw new Error("services.readFile returned null/undefined");
      }

      const FRESH_PATH = "/diag-fresh.dwg";
      console.log("[fresh-save] phase 3: construct OdString filename");
      pName = new mod.OdString(FRESH_PATH);

      console.log("[fresh-save] phase 4: freshDb.writeFile1(", FRESH_PATH, ")");
      const t4 = performance.now();
      freshDb.writeFile1(pName, 0, 33, false, 16);
      const t5 = performance.now();
      console.log(
        "[fresh-save] phase 4 done in",
        (t5 - t4).toFixed(1),
        "ms",
      );

      const info = winFS.analyzePath(FRESH_PATH);
      const size =
        info?.exists && winFS.stat ? winFS.stat(FRESH_PATH).size : "?";
      console.log(
        "[fresh-save] phase 5: result → exists=",
        info?.exists,
        "size=",
        size,
      );

      if (info?.exists && typeof winFS.readFile === "function") {
        const bytes = winFS.readFile(FRESH_PATH);
        console.log(
          "[fresh-save] phase 6: window.FS.readFile returned",
          bytes.length,
          "bytes",
        );
        if (bytes.length > 0) {
          // Download for inspection (same naming pattern as the real save).
          const blob = new Blob([bytes.buffer as ArrayBuffer], {
            type: "application/acad",
          });
          const url = URL.createObjectURL(blob);
          const ts = new Date()
            .toISOString()
            .replace(/[-:T]/g, "")
            .replace(/\..+$/, "")
            .replace(/(\d{8})(\d{6})/, "$1-$2");
          const filename = `diag-fresh-template-${ts}.dwg`;
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          console.log("[fresh-save] DONE — downloaded", filename);
        } else {
          console.warn(
            "[fresh-save] CONCLUSION: writeFile1 produces 0 bytes even on a freshly-loaded template. " +
              "This is an SDK-build/licensing issue, not a db-state issue. Check Protection Server logs.",
          );
        }
      }
    } catch (e) {
      console.error("[fresh-save] EXCEPTION CAUGHT");
      console.error("[fresh-save]   raw:", e);
      console.error("[fresh-save]   String(e):", String(e));
      if (typeof mod.getExceptionMessage === "function") {
        try {
          console.error(
            "[fresh-save]   Module.getExceptionMessage:",
            mod.getExceptionMessage(e),
          );
        } catch (probeErr) {
          console.error(
            "[fresh-save]   getExceptionMessage threw:",
            probeErr,
          );
        }
      }
    } finally {
      if (pName) {
        try {
          pName.delete();
        } catch (err) {
          console.warn("[fresh-save] OdString.delete threw:", err);
        }
      }
      if (services) {
        try {
          services.uninitialize();
        } catch (err) {
          console.warn("[fresh-save] services.uninitialize threw:", err);
        }
        try {
          services.delete?.();
        } catch (err) {
          console.warn("[fresh-save] services.delete threw:", err);
        }
      }
    }
  }

  // Click pick: phase 1 (screen→world coord log) + phase 2 (select +
  // highlight via OdDbSelectionSet.select1 — example 07 canonical path).
  function handleCanvasPick(ev: MouseEvent): void {
    const app = appRef.current;
    const mod = window.Module;
    if (!app || !mod) return;

    const x = ev.offsetX;
    const y = ev.offsetY;

    let worldPt: OdaPoint3d | null = null;

    // ── Phase 1: screen → world transform ──────────────────────────────
    try {
      const device = app.getDevice();
      const view = device.viewAt(0);
      worldPt = screenToWorld(mod, view, x, y);
      console.log(
        "[pick] canvas:",
        x,
        y,
        "world:",
        worldPt.x,
        worldPt.y,
        worldPt.z,
      );
    } catch (err) {
      console.error("[pick] phase 1 (screenToWorld) threw:", err);
      if (worldPt) worldPt.delete();
      return;
    }

    // ── Move-mode branch: CAD-style two-click MOVE. The user must have a
    // block selected (clicked it before entering move mode). The FIRST
    // click sets the base (grab) point; the SECOND sets the destination,
    // and the block translates by (destination − base). Auto-exits after
    // the destination click.
    if (moveModeRef.current) {
      // No selection → nothing to move; bail out of move mode.
      if (selectedIdsRef.current.size === 0) {
        console.log("[move] no selection, exiting move mode");
        worldPt.delete();
        setMoveHint("");
        moveBasePointRef.current = null;
        if (onExitMoveModeRef.current) onExitMoveModeRef.current();
        return;
      }

      if (!moveBasePointRef.current) {
        // First click → record the base point and wait for the
        // destination. Stay in move mode.
        moveBasePointRef.current = { x: worldPt.x, y: worldPt.y };
        console.log("[move] base point:", worldPt.x, worldPt.y);
        worldPt.delete();
        setMoveHint("Pick destination point");
        return;
      }

      // Second click → translate by (destination − base), then exit.
      const base = moveBasePointRef.current;
      const dx = worldPt.x - base.x;
      const dy = worldPt.y - base.y;
      worldPt.delete();
      moveBasePointRef.current = null;
      setMoveHint("");
      handleMoveSelectedByDelta(dx, dy);
      if (onExitMoveModeRef.current) onExitMoveModeRef.current();
      return;
    }

    // ── Add-mode branch: drop a tracker at worldPt instead of selecting.
    if (addModeRef.current) {
      try {
        handleAddTrackerAtPoint(worldPt);
      } finally {
        worldPt.delete();
      }
      return;
    }

    // ── Phase 2: pick via OdDbSelectionSet.select1 + highlight ──────
    try {
      const db = app.getDb();
      const pts = new mod.OdGePoint3dArray();
      pts.push_back(worldPt);
      const selSet = mod.OdDbSelectionSet.select1(
        db.activeViewportId(),
        1,
        pts.asArrayPtr(),
        window.Mode.kPoint,
        window.SubentSelectionMode.kDisableSubents,
        null,
      );
      pts.delete();

      const ids = selSet.objectIdArray();
      const numHits = ids.size();

      if (numHits > 0) {
        const hitId = ids.get(0);
        const handleStr = handleStringFor(hitId);
        // Only trackers (in the idMap) are selectable — roads / inverters
        // / boundary have no server id and can't be edited.
        if (!handleStr || !idMapRef.current.has(handleStr)) {
          console.log("[pick] hit a non-tracker entity, ignoring");
          hitId.delete();
        } else if (selectedIdsRef.current.has(handleStr)) {
          // ── Toggle OFF: clicking an already-selected tracker unhighlights
          // it and removes it from the set.
          const existing = selectedIdsRef.current.get(handleStr)!;
          setEntityHighlight(existing, false);
          existing.delete();
          selectedIdsRef.current.delete(handleStr);
          hitId.delete();
          console.log("[pick] toggled OFF", handleStr, "→", selectedIdsRef.current.size, "selected");
        } else {
          // ── Toggle ON: highlight it and add it to the set.
          setEntityHighlight(hitId, true);
          selectedIdsRef.current.set(handleStr, hitId);
          console.log("[pick] toggled ON", handleStr, "→", selectedIdsRef.current.size, "selected");
        }
      } else {
        // Clicking empty space clears the whole selection (CAD behaviour).
        console.log("[pick] MISS — clearing selection");
        clearSelection();
      }

      ids.delete();
      selSet.delete();
      requestRepaint();
    } catch (err) {
      console.error("[pick] phase 2 (select+highlight) threw:", err);
    } finally {
      worldPt.delete();
    }
  }

  return (
    <div
      style={{
        position: "relative",
        height: "100vh",
        width: "100%",
        background: "#ffffff",
        overflow: "hidden",
      }}
    >
      <canvas
        ref={canvasRef}
        id="canvas"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
      {status === "loading" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6b7280",
            fontSize: 13,
            pointerEvents: "none",
            gap: 10,
            flexDirection: "column",
          }}
        >
          <span style={loadingSpinnerStyle} aria-hidden />
          <span data-testid="viewer-loading-message">{loadingMsg}</span>
        </div>
      )}
      {moveHint && (
        <div
          data-testid="move-hint"
          style={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(217,119,6,0.95)",
            color: "#ffffff",
            font: "13px/1.4 ui-monospace, monospace",
            padding: "6px 12px",
            borderRadius: 6,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            zIndex: 6,
            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          }}
        >
          ↔ {moveHint} · ESC to cancel
        </div>
      )}
      {status === "error" && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            alignItems: "center",
            justifyContent: "center",
            color: "#dc2626",
            fontSize: 14,
            padding: 16,
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 460 }}>
            The viewer hit an error and the 3D engine needs a clean reload to
            recover.
            {errorMsg ? (
              <div style={{ marginTop: 6, color: "#991b1b", fontSize: 12 }}>
                {errorMsg}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: "#635bff",
              color: "white",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Reload viewer
          </button>
        </div>
      )}
    </div>
  );
}
